import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  CalendarRange, Save, Loader2, Plus, Trash2, Sparkles, Wand2, Eraser, RotateCcw,
  Users, GraduationCap, Send, CheckCircle2, AlertTriangle, Clock, X, FileDown, FileStack,
  ArrowLeftRight, BarChart3, Lock, LockOpen, CalendarClock, ListOrdered, RefreshCw,
  Download, FileText, Wrench, Target, Ban,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { TeacherPicker } from '../../components/school/TeacherPicker';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, InlineSpinner, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const cellKey = (day, seq) => `${day}_${seq}`;

// A pill in the Missing Lessons bar. Clicking it highlights the legal empty cells
// for that lesson. Used in By Class, Student and By Teacher views.
const MissingChip = ({ l, active, onClick, showSection, t }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${active
      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
      : 'border-border bg-background hover:bg-accent text-foreground'}`}
    data-testid={`tt-missing-chip-${l.subject_id}`}>
    <span className="truncate max-w-[160px]">{l.label || l.subject_name}</span>
    {showSection && l.section_name && (
      <span className="text-[10px] text-muted-foreground">· {l.section_name}</span>
    )}
    <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-emerald-500/20' : 'bg-primary/10 text-primary'}`}>
      {l._missing} {t('timetable.hoursLeft', 'left')}
    </span>
  </button>
);

// A (non-break) period runs on a given day when it has no `days` restriction,
// or that day is listed. Days outside the list are "off" (e.g. Friday P6/P7).
const periodRunsOn = (period, dayKey) => {
  if (period?.is_break) return true;
  const d = period?.days;
  if (!d || d.length === 0) return true;
  return d.includes(dayKey);
};

// html2canvas captures the DOM synchronously, so any <img> that has not finished
// loading/decoding (e.g. the branded logo) is drawn blank. Await every image inside
// the print container before we snapshot it. A short per-image timeout guarantees we
// never hang the export if an asset is unreachable.
const awaitImages = async (el) => {
  if (!el) return;
  const imgs = Array.from(el.querySelectorAll('img'));
  await Promise.all(imgs.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      setTimeout(done, 2500);
    });
  }));
};

// Render a printable sheet DOM node into a jsPDF doc that ALWAYS fits on ONE A4
// landscape page. We capture the node with html2canvas, then place the image scaled
// to fit inside the printable area (both width AND height). Dense timetables (many
// periods for a class + its paired class) used to overflow onto a 2nd page under
// html2pdf's auto-pagination — this uniform down-scale guarantees a single page.
const PDF_PAGE = { w: 297, h: 210 };            // A4 landscape (mm)
const PDF_MARGIN = { x: 8, top: 8, bottom: 10 }; // mm
const nodeToOnePagePdf = async (node) => {
  const html2canvas = (await import('html2canvas')).default;
  const { jsPDF } = await import('jspdf');
  const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 1400 });
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const contentW = PDF_PAGE.w - PDF_MARGIN.x * 2;
  const contentH = PDF_PAGE.h - PDF_MARGIN.top - PDF_MARGIN.bottom;
  const ratio = canvas.height / canvas.width; // aspect (h / w)
  let imgW = contentW;
  let imgH = imgW * ratio;
  if (imgH > contentH) { imgH = contentH; imgW = imgH / ratio; } // constrain by height when tall
  const x = PDF_MARGIN.x + (contentW - imgW) / 2; // centre horizontally
  const y = PDF_MARGIN.top;
  pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', x, y, imgW, imgH);
  return pdf;
};



// Group a section's subjects into scheduling units for the Lesson Requirements table.
// A Value-Unit (U.V) row is unique by (uv_group, teacher): subjects that share a UV
// but are assigned to DIFFERENT teachers form SEPARATE rows (one per teacher), each
// keeping the UV label. Subjects without a uv_group each stay individual.
// ``teacherOf(subject)`` resolves the effective teacher id (subject's own teacher, then
// the class-wide name→teacher fallback). Mirrors backend rebuild grouping.
function buildUnits(subjects, teacherOf = () => null) {
  const bySignature = {};
  const deduped = [];
  (subjects || []).forEach((s) => {
    if (!s?.name || bySignature[s.name]) return; // de-dupe class-wide + section rows by name
    bySignature[s.name] = true;
    deduped.push(s);
  });
  const groups = {};
  const order = [];
  deduped.forEach((s) => {
    const uv = (s.uv_group || '').trim();
    const tid = teacherOf(s) || '';
    // Composite key so a multi-teacher UV splits into one row per teacher.
    const key = uv ? `uv:${uv}::${tid}` : `subj:${s.id}`;
    if (!groups[key]) { groups[key] = { type: uv ? 'uv' : 'subj', uv, teacherId: tid || null, members: [] }; order.push(key); }
    groups[key].members.push(s);
  });
  return order.map((key) => {
    const g = groups[key];
    if (g.type === 'uv') {
      const rep = [...g.members].sort((a, b) =>
        ((a.uv_order ?? 9999) - (b.uv_order ?? 9999)) ||
        String(a.name || '').localeCompare(String(b.name || '')))[0];
      return { key, type: 'uv', label: g.uv, uv_group: g.uv, teacherId: g.teacherId, rep, members: g.members, memberIds: g.members.map((m) => m.id) };
    }
    const s = g.members[0];
    return { key, type: 'subj', label: s.name, uv_group: null, teacherId: teacherOf(s) || null, rep: s, members: g.members, memberIds: [s.id] };
  });
}

export default function TimetablePage() {
  const { user } = useAuth();
  const { t } = useT();
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const canEdit = ['super_admin', 'admin'].includes(user?.role);

  const [config, setConfig] = useState(null);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [tab, setTab] = useState('setup');

  // ---- bootstrap ----
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      setLoading(true);
      try {
        const [cfg, cls, staffRes] = await Promise.all([
          schoolApi.ttGetConfig({ organization_id: orgId, academic_year_id: selectedYearId }),
          schoolApi.listClasses(orgId, selectedYearId),
          schoolApi.listStaff(orgId, { limit: 2000 }),
        ]);
        setConfig(cfg);
        setClasses(cls || []);
        // Teachers = Teacher/Staff accounts (same source as Subjects assignment), so
        // subject.teacher_id lines up with these ids and the picker is never empty.
        setTeachers(((staffRes && staffRes.staff) || []).map((s) => ({
          id: s.id, name: s.name || s.name_en || s.code, code: s.code, mobile: s.mobile,
        })));
      } catch (e) {
        toast.error('Failed to load timetable data');
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, selectedYearId]);

  const activeDays = useMemo(() => (config?.days || []).filter((d) => d.active), [config]);
  const teachPeriods = useMemo(() => (config?.periods || []).filter((p) => !p.is_break).map((p) => p.seq), [config]);

  // ============================ SETUP: bell schedule ============================
  const toggleDay = (key) => setConfig((c) => ({ ...c, days: c.days.map((d) => d.key === key ? { ...d, active: !d.active } : d) }));
  const updatePeriod = (seq, field, value) => setConfig((c) => ({ ...c, periods: c.periods.map((p) => p.seq === seq ? { ...p, [field]: value } : p) }));
  const addPeriod = () => setConfig((c) => {
    const nextSeq = Math.max(0, ...c.periods.map((p) => p.seq)) + 1;
    return { ...c, periods: [...c.periods, { seq: nextSeq, label: `P${nextSeq}`, start: '', end: '', is_break: false }] };
  });
  const removePeriod = (seq) => setConfig((c) => ({ ...c, periods: c.periods.filter((p) => p.seq !== seq) }));
  // Restrict a period to specific teaching days. Empty/undefined `days` = runs every active day.
  const togglePeriodDay = (seq, dayKey) => setConfig((c) => {
    const activeKeys = c.days.filter((d) => d.active).map((d) => d.key);
    return {
      ...c,
      periods: c.periods.map((p) => {
        if (p.seq !== seq) return p;
        const current = (p.days && p.days.length) ? p.days.filter((k) => activeKeys.includes(k)) : activeKeys.slice();
        const next = current.includes(dayKey) ? current.filter((k) => k !== dayKey) : [...current, dayKey];
        const isAll = activeKeys.length > 0 && activeKeys.every((k) => next.includes(k));
        return { ...p, days: isAll ? undefined : next };
      }),
    };
  });

  const saveConfig = async () => {
    if (!selectedYearId) { toast.error(t('timetable.selectYearFirst', 'Select an academic year first')); return; }
    setSavingCfg(true);
    try {
      const saved = await schoolApi.ttSaveConfig({ organization_id: orgId, academic_year_id: selectedYearId, days: config.days, periods: config.periods });
      setConfig(saved);
      toast.success(t('timetable.scheduleSaved', 'Bell schedule saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save schedule');
    } finally { setSavingCfg(false); }
  };

  const resetConfig = async () => {
    if (!selectedYearId) { toast.error(t('timetable.selectYearFirst', 'Select an academic year first')); return; }
    setResetting(true);
    try {
      const def = await schoolApi.ttResetConfig({ organization_id: orgId, academic_year_id: selectedYearId });
      setConfig(def);
      toast.success(t('timetable.scheduleReset', 'Bell schedule reset to default'));
      setResetOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset schedule');
    } finally { setResetting(false); }
  };

  if (loading) return <div className="p-8"><InlineSpinner /></div>;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="timetable-page">
      <PageHeader icon={CalendarRange} title={t('nav.timetable', 'Timetable')} subtitle={t('timetable.subtitle', 'Plan classes, generate schedules and resolve clashes')} />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="timetable-tabs">
          <TabsTrigger value="setup" data-testid="tab-setup">{t('timetable.setup', 'Setup')}</TabsTrigger>
          <TabsTrigger value="grid" data-testid="tab-grid">{t('timetable.timetable', 'Timetable')}</TabsTrigger>
          <TabsTrigger value="assistant" data-testid="tab-assistant"><Sparkles className="w-4 h-4 mr-1" /> {t('timetable.aiAssistant', 'AI Assistant')}</TabsTrigger>
        </TabsList>

        {/* ============================ SETUP ============================ */}
        <TabsContent value="setup" className="space-y-4 mt-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2"><Clock className="w-4 h-4" /> {t('timetable.bellSchedule', 'Bell Schedule')}</h3>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <Button onClick={() => setResetOpen(true)} disabled={savingCfg || resetting} size="sm" variant="outline" className="gap-2" data-testid="tt-reset-config">
                    <RotateCcw className="w-4 h-4" /> {t('timetable.resetDefault', 'Reset to Default')}
                  </Button>
                  <Button onClick={saveConfig} disabled={savingCfg} size="sm" className="gap-2" data-testid="tt-save-config">{savingCfg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('common.save', 'Save')}</Button>
                </div>
              )}
            </div>
            <div className="mb-5">
              <p className="text-sm font-medium mb-2">{t('timetable.teachingDays', 'Teaching Days')}</p>
              <div className="flex flex-wrap gap-2">
                {config.days.map((d) => (
                  <button key={d.key} type="button" disabled={!canEdit} onClick={() => toggleDay(d.key)}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${d.active ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-border'}`}
                    data-testid={`tt-day-${d.key}`}>{d.label}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">{t('timetable.periods', 'Periods')}</p>
                {canEdit && <Button onClick={addPeriod} size="sm" variant="outline" className="gap-1 h-8" data-testid="tt-add-period"><Plus className="w-3 h-3" /> {t('timetable.addPeriod', 'Add')}</Button>}
              </div>
              <div className="space-y-2">
                {config.periods.map((p) => (
                  <div key={p.seq} className="flex items-center gap-2 flex-wrap" data-testid={`tt-period-${p.seq}`}>
                    <span className="w-8 text-xs text-muted-foreground tabular-nums">#{p.seq}</span>
                    <Input className="h-8 w-28" value={p.label} disabled={!canEdit} onChange={(e) => updatePeriod(p.seq, 'label', e.target.value)} placeholder="Label" />
                    <Input className="h-8 w-24" type="time" value={p.start || ''} disabled={!canEdit} onChange={(e) => updatePeriod(p.seq, 'start', e.target.value)} />
                    <span className="text-muted-foreground">–</span>
                    <Input className="h-8 w-24" type="time" value={p.end || ''} disabled={!canEdit} onChange={(e) => updatePeriod(p.seq, 'end', e.target.value)} />
                    <label className="flex items-center gap-1.5 text-xs ml-2">
                      <Switch checked={p.is_break} disabled={!canEdit} onCheckedChange={(v) => updatePeriod(p.seq, 'is_break', v)} data-testid={`tt-period-break-${p.seq}`} />
                      {t('timetable.break', 'Break')}
                    </label>
                    {!p.is_break && (
                      <div className="flex items-center gap-1 ml-1" data-testid={`tt-period-days-${p.seq}`}>
                        <span className="text-[11px] text-muted-foreground mr-0.5">{t('timetable.runsOn', 'Runs on')}:</span>
                        {config.days.filter((d) => d.active).map((d) => {
                          const on = !(p.days && p.days.length) || p.days.includes(d.key);
                          return (
                            <button key={d.key} type="button" disabled={!canEdit}
                              onClick={() => togglePeriodDay(p.seq, d.key)}
                              className={`px-1.5 py-0.5 rounded text-[11px] border transition-colors ${on ? 'bg-primary/10 text-primary border-primary/40' : 'bg-muted text-muted-foreground/60 border-border line-through'}`}
                              data-testid={`tt-period-${p.seq}-day-${d.key}`}>
                              {String(d.label).slice(0, 3)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removePeriod(p.seq)} data-testid={`tt-remove-period-${p.seq}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <LessonsSetup orgId={orgId} academicYearId={selectedYearId} classes={classes} teachers={teachers} canEdit={canEdit} t={t} />

          <ProgramWindowsSetup orgId={orgId} academicYearId={selectedYearId} classes={classes} config={config} canEdit={canEdit} t={t} />

          <TeacherAvailabilitySetup orgId={orgId} academicYearId={selectedYearId} teachers={teachers} config={config} activeDays={activeDays} canEdit={canEdit} t={t} />

          <MergedSubjectsSetup orgId={orgId} academicYearId={selectedYearId} classes={classes} canEdit={canEdit} t={t} />

          <SubjectRulesSetup orgId={orgId} academicYearId={selectedYearId} classes={classes} config={config} canEdit={canEdit} t={t} />

          <CyclesPairingsSetup orgId={orgId} academicYearId={selectedYearId} classes={classes} canEdit={canEdit} t={t} />

          <Dialog open={resetOpen} onOpenChange={(o) => { if (!resetting) setResetOpen(o); }}>
            <DialogContent data-testid="tt-reset-dialog">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="w-4 h-4" /> {t('timetable.resetDefault', 'Reset to Default')}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground py-1">{t('timetable.resetConfirm', 'This replaces your current bell schedule with the default ELI schedule (Mon–Thu P1–P7; Friday ends at Lunch, 13:00). This cannot be undone.')}</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting} data-testid="tt-reset-cancel">{t('common.cancel', 'Cancel')}</Button>
                <Button onClick={resetConfig} disabled={resetting} className="gap-2" data-testid="tt-reset-confirm">
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} {t('timetable.resetDefault', 'Reset to Default')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ============================ TIMETABLE GRID ============================ */}
        <TabsContent value="grid" className="mt-4">
          <TimetableGrid orgId={orgId} academicYearId={selectedYearId} classes={classes} teachers={teachers} config={config}
            activeDays={activeDays} canEdit={canEdit} t={t} onGoToSetup={() => setTab('setup')} />
        </TabsContent>

        {/* ============================ AI ASSISTANT ============================ */}
        <TabsContent value="assistant" className="mt-4">
          <AiAssistant orgId={orgId} academicYearId={selectedYearId} classes={classes} config={config} activeDays={activeDays} canEdit={canEdit} t={t} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ----------------------------- Lessons Setup ----------------------------- */
function LessonsSetup({ orgId, academicYearId, classes, teachers, canEdit, t }) {
  const [classId, setClassId] = useState('');
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [teacherByName, setTeacherByName] = useState({});
  const [subjectTeacherOpts, setSubjectTeacherOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowStatus, setRowStatus] = useState({}); // unit.key -> 'saving' | 'saved' | 'error'
  const [savingAll, setSavingAll] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildPreview, setRebuildPreview] = useState(null);

  // Dropdown options = configured teachers + any teacher actually assigned to a subject
  // (which may be a staff account not present in the school_teachers list).
  const teacherOptions = useMemo(() => {
    const byId = {};
    (teachers || []).forEach((t) => { byId[t.id] = { id: t.id, name: t.name }; });
    subjectTeacherOpts.forEach((t) => { if (t.id && !byId[t.id]) byId[t.id] = t; });
    return Object.values(byId);
  }, [teachers, subjectTeacherOpts]);

  useEffect(() => {
    if (!classId) { setSections([]); setSectionId(''); return; }
    schoolApi.listSections(orgId, classId, academicYearId).then((s) => { setSections(s || []); setSectionId((s && s[0]?.id) || ''); });
  }, [classId, orgId, academicYearId]);

  const reload = useCallback(async () => {
    if (!sectionId) { setSubjects([]); setLessons([]); return; }
    setLoading(true);
    try {
      const [subs, less, allSubs] = await Promise.all([
        schoolApi.listSubjects(orgId, classId, sectionId, academicYearId),
        schoolApi.ttListLessons({ organization_id: orgId, section_id: sectionId, academic_year_id: academicYearId }),
        schoolApi.listSubjects(orgId, classId, undefined, academicYearId),
      ]);
      // Teachers are often assigned on the class-wide subject row (no section), while
      // section-specific rows have no teacher. Build a name -> teacher fallback map
      // and collect the assigned teachers (id + resolved name) for the dropdown options.
      const tmap = {};
      const opts = {};
      (allSubs || []).forEach((r) => {
        if (r.teacher_id) {
          if (!tmap[r.name]) tmap[r.name] = r.teacher_id;
          if (!opts[r.teacher_id]) opts[r.teacher_id] = { id: r.teacher_id, name: r.teacher_name || r.teacher_id };
        }
      });
      setTeacherByName(tmap);
      setSubjectTeacherOpts(Object.values(opts));
      setSubjects(subs || []);
      setLessons(less || []);
    } finally { setLoading(false); }
  }, [orgId, classId, sectionId, academicYearId]);
  useEffect(() => { reload(); }, [reload]);

  const lessonFor = (unit) => lessons.find((l) => unit.memberIds.includes(l.subject_id));
  const resolvedTeacher = (unit) => {
    const l = lessonFor(unit);
    if (l && l.teacher_id) return l.teacher_id;
    for (const m of unit.members) { if (m.teacher_id) return m.teacher_id; }
    for (const m of unit.members) { if (teacherByName[m.name]) return teacherByName[m.name]; }
    return null;
  };
  // Default periods/week from hours_per_week (set in class assignment). For a Value
  // Unit we sum the member subjects' hours until an explicit lesson overrides it.
  const defaultPpw = (unit) =>
    unit.members.reduce((a, m) => a + Math.round(Number(m.hours_per_week) || 0), 0);
  const resolvedPpw = (unit) => {
    const l = lessonFor(unit);
    return (l && l.periods_per_week != null) ? l.periods_per_week : defaultPpw(unit);
  };
  const resolvedMaxPerDay = (unit) => {
    const l = lessonFor(unit);
    return (l && l.max_per_day != null) ? l.max_per_day : '';
  };

  const saveLesson = async (unit, patch = {}) => {
    const cur = lessonFor(unit) || {};
    // Reuse the existing lesson's key subject when present so a UV keeps ONE row.
    const keyId = cur.subject_id || unit.rep.id;
    const body = {
      organization_id: orgId, class_id: classId, section_id: sectionId, subject_id: keyId,
      uv_group: unit.uv_group || undefined,
      teacher_id: patch.teacher_id !== undefined ? patch.teacher_id : (cur.teacher_id || resolvedTeacher(unit)),
      periods_per_week: patch.periods_per_week !== undefined ? patch.periods_per_week
        : (cur.periods_per_week != null ? cur.periods_per_week : defaultPpw(unit)),
      double: patch.double !== undefined ? patch.double : (cur.double || false),
      max_per_day: patch.max_per_day !== undefined ? patch.max_per_day : (cur.max_per_day ?? null),
    };
    setRowStatus((s) => ({ ...s, [unit.key]: 'saving' }));
    try {
      const saved = await schoolApi.ttSaveLesson(body);
      setLessons((ls) => { const rest = ls.filter((l) => !unit.memberIds.includes(l.subject_id)); return [...rest, saved]; });
      setRowStatus((s) => ({ ...s, [unit.key]: 'saved' }));
      setTimeout(() => setRowStatus((s) => {
        if (s[unit.key] !== 'saved') return s;
        const n = { ...s }; delete n[unit.key]; return n;
      }), 2000);
      return true;
    } catch (e) {
      setRowStatus((s) => ({ ...s, [unit.key]: 'error' }));
      toast.error('Failed to save lesson');
      return false;
    }
  };

  const teacherOfSubject = useCallback(
    (s) => (s && (s.teacher_id || teacherByName[s.name])) || null,
    [teacherByName],
  );
  const units = useMemo(() => buildUnits(subjects, teacherOfSubject), [subjects, teacherOfSubject]);
  const uvUnits = units.filter((u) => u.type === 'uv');
  const subjUnits = units.filter((u) => u.type === 'subj');
  const totalPpw = units.reduce((a, u) => a + resolvedPpw(u), 0);

  // Persist every U.V row explicitly (so auto-generate is deterministic, not fallback-based).
  const saveAll = async () => {
    setSavingAll(true);
    try {
      for (const u of units) { await saveLesson(u, {}); }
      toast.success(t('timetable.allSaved', 'All lesson requirements saved'));
    } finally { setSavingAll(false); }
  };

  // One-time cleanup: re-align every lesson/entry Value-Unit tag to its subject.
  const resyncUv = async () => {
    setResyncing(true);
    try {
      const res = await schoolApi.ttResyncUvGroups({ organization_id: orgId, academic_year_id: academicYearId });
      toast.success(`${t('timetable.uvResynced', 'Value-Unit tags re-synced')}: ${res?.lessons_updated ?? 0} ${t('timetable.lessonsWord', 'lessons')}, ${res?.entries_updated ?? 0} ${t('timetable.entriesWord', 'entries')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to re-sync');
    } finally { setResyncing(false); }
  };

  // Rebuild: delete all requirements for the selected class and re-import from
  // subject assignments. Always previews (dry-run) before applying.
  const openRebuild = async () => {
    if (!classId) { toast.error(t('timetable.selectClassFirst', 'Select a class first')); return; }
    setRebuilding(true);
    try {
      const preview = await schoolApi.ttRebuildLessons({ organization_id: orgId, academic_year_id: academicYearId, class_id: classId, dry_run: true });
      setRebuildPreview(preview);
      setRebuildOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to preview rebuild');
    } finally { setRebuilding(false); }
  };

  const doRebuild = async () => {
    if (!classId) return;
    setRebuilding(true);
    try {
      const res = await schoolApi.ttRebuildLessons({ organization_id: orgId, academic_year_id: academicYearId, class_id: classId, dry_run: false });
      setRebuildOpen(false); setRebuildPreview(null);
      toast.success(`${t('timetable.rebuilt', 'Lesson Requirements rebuilt')}: ${res?.lessons_deleted ?? 0} ${t('timetable.deletedWord', 'deleted')}, ${res?.lessons_created ?? 0} ${t('timetable.createdWord', 'created')}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to rebuild');
    } finally { setRebuilding(false); }
  };

  const selectedClassName = (classes || []).find((c) => c.id === classId)?.name;

  return (
    <Card className="p-5" data-testid="tt-lessons-setup">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="font-semibold mb-1">{t('timetable.lessonReqs', 'Lesson Requirements')}</h3>
          <p className="text-xs text-muted-foreground mb-4">{t('timetable.lessonReqsHint', 'Planned by Value Unit / Component (U.V) — subjects grouped on report cards are scheduled together. Changes save automatically; use Save All to persist every row.')}</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" className="gap-2" onClick={openRebuild} disabled={rebuilding || !classId} data-testid="tt-rebuild-lessons" title={t('timetable.rebuildHint', 'Delete this class\u2019s requirements and re-import from subject assignments')}>
              {rebuilding && !rebuildOpen ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} {t('timetable.rebuildFromAssignments', 'Rebuild from Assignments')}
            </Button>
            <Button size="sm" variant="ghost" className="gap-2 text-muted-foreground" onClick={resyncUv} disabled={resyncing} data-testid="tt-resync-uv" title={t('timetable.uvResyncHint', 'Re-align every lesson & entry Value-Unit tag to its subject')}>
              {resyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {t('timetable.resyncUv', 'Re-sync UV')}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={rebuildOpen} onOpenChange={(o) => { if (!rebuilding) { setRebuildOpen(o); if (!o) setRebuildPreview(null); } }}>
        <DialogContent data-testid="tt-rebuild-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="w-4 h-4" /> {t('timetable.rebuildTitle', 'Rebuild from Assignments')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <p className="text-muted-foreground">
              {t('timetable.rebuildIntro', 'This permanently deletes ALL current Lesson Requirements for this class (every section) and re-imports fresh ones from the subject assignments (periods/week = each subject\u2019s hours/week). This cannot be undone.')}
            </p>
            {rebuildPreview && (
              <div className="rounded-md border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{rebuildPreview.class_name || selectedClassName}</span>
                  <Badge variant="outline">{rebuildPreview.sections} {t('timetable.sectionsWord', 'sections')}</Badge>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-destructive" data-testid="tt-rebuild-delete-count">{t('timetable.willDelete', 'Will delete')}: {rebuildPreview.lessons_to_delete}</span>
                  <span className="text-primary" data-testid="tt-rebuild-create-count">{t('timetable.willCreate', 'Will create')}: {rebuildPreview.lessons_to_create}</span>
                </div>
                <div className="max-h-40 overflow-auto space-y-1">
                  {(rebuildPreview.per_section || []).map((ps) => (
                    <div key={ps.section_id} className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{ps.section_name || ps.section_id?.slice(0, 6)}</span>
                      <span>{ps.created} {t('timetable.reqsWord', 'requirements')}{ps.skipped ? ` · ${ps.skipped} ${t('timetable.skippedWord', 'skipped')}` : ''}</span>
                    </div>
                  ))}
                </div>
                {rebuildPreview.skipped_no_hours > 0 && (
                  <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-xs text-amber-700 dark:text-amber-400" data-testid="tt-rebuild-skipped-note">
                    <div className="font-medium mb-0.5">
                      {rebuildPreview.skipped_no_hours} {t('timetable.subjectsSkipped', 'subject(s) skipped (no hours set in assignment)')}
                    </div>
                    <div className="text-muted-foreground">{(rebuildPreview.skipped_names || []).join(' · ')}</div>
                    <div className="mt-1">{t('timetable.skippedHint', 'Set hours/week on these subjects in the class assignment, then rebuild to include them.')}</div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRebuildOpen(false)} disabled={rebuilding} data-testid="tt-rebuild-cancel">{t('common.cancel', 'Cancel')}</Button>
            <Button className="gap-2" onClick={doRebuild} disabled={rebuilding} data-testid="tt-rebuild-confirm">
              {rebuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} {t('timetable.rebuildConfirm', 'Delete & Rebuild')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="w-56" data-testid="tt-lesson-class"><SelectValue placeholder={t('timetable.selectClass', 'Select class')} /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sectionId} onValueChange={setSectionId} disabled={!sections.length}>
          <SelectTrigger className="w-48" data-testid="tt-lesson-section"><SelectValue placeholder={t('timetable.selectSection', 'Select section')} /></SelectTrigger>
          <SelectContent>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        {sectionId && <Badge variant="outline" className="self-center">{t('timetable.totalPerWeek', 'Total/week')}: {totalPpw}</Badge>}
        {sectionId && units.length > 0 && canEdit && (
          <Button size="sm" variant="outline" className="gap-2 self-center ml-auto" onClick={saveAll} disabled={savingAll} data-testid="tt-lesson-save-all">
            {savingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('timetable.saveAll', 'Save All')}
          </Button>
        )}
      </div>

      {loading ? <InlineSpinner /> : !sectionId ? (
        <EmptyState icon={CalendarRange} title={t('timetable.pickClassSection', 'Select a class and section')} description={t('timetable.pickClassSectionDesc', 'Choose a section to define how many periods per week each Value Unit needs.')} />
      ) : units.length === 0 ? (
        <EmptyState icon={CalendarRange} title={t('common.noData', 'No data')} description={t('timetable.noSubjects', 'No subjects for this section.')} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-4">{t('subjects.uvColumn', 'Value Unit / Component')}</th>
              <th className="py-2 px-2">{t('timetable.teacher', 'Teacher')}</th>
              <th className="py-2 px-2 w-32">{t('timetable.perWeek', 'Periods/week')}</th>
              <th className="py-2 px-2 w-28">{t('timetable.maxPerDay', 'Max/day')}</th>
              <th className="py-2 px-2 w-24">{t('timetable.double', 'Double')}</th>
            </tr></thead>
            <tbody>
              {uvUnits.map((u) => <LessonRow key={u.key} unit={u} lessonFor={lessonFor} resolvedTeacher={resolvedTeacher} resolvedPpw={resolvedPpw} resolvedMaxPerDay={resolvedMaxPerDay} saveLesson={saveLesson} teacherOptions={teacherOptions} canEdit={canEdit} status={rowStatus[u.key]} showMembers />)}
              {subjUnits.length > 0 && (
                <tr data-testid="tt-lesson-ungrouped-heading">
                  <td colSpan={5} className="pt-5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('timetable.ungrouped', 'Ungrouped / No U.V')}
                  </td>
                </tr>
              )}
              {subjUnits.map((u) => <LessonRow key={u.key} unit={u} lessonFor={lessonFor} resolvedTeacher={resolvedTeacher} resolvedPpw={resolvedPpw} resolvedMaxPerDay={resolvedMaxPerDay} saveLesson={saveLesson} teacherOptions={teacherOptions} canEdit={canEdit} status={rowStatus[u.key]} />)}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* One lesson-requirement row (a Value Unit or an ungrouped subject). */
function LessonRow({ unit, lessonFor, resolvedTeacher, resolvedPpw, resolvedMaxPerDay, saveLesson, teacherOptions, canEdit, status, showMembers }) {
  const l = lessonFor(unit) || {};
  const tid = `${unit.rep.id}`;
  return (
    <tr className="border-b border-border/50" data-testid={`tt-lesson-row-${tid}`}>
      <td className="py-2 pr-4">
        <div className="flex items-center gap-2">
          <span className="font-medium">{unit.label}</span>
          {status === 'saving' && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" data-testid={`tt-lesson-status-saving-${tid}`} />}
          {status === 'saved' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" data-testid={`tt-lesson-status-saved-${tid}`} />}
          {status === 'error' && <AlertTriangle className="w-3.5 h-3.5 text-destructive" data-testid={`tt-lesson-status-error-${tid}`} />}
        </div>
        {showMembers && unit.members.length > 0 && (
          <div className="text-[11px] text-muted-foreground mt-0.5">{unit.members.map((m) => m.name).join(' · ')}</div>
        )}
      </td>
      <td className="py-2 px-2">
        <Select value={l.teacher_id || resolvedTeacher(unit) || ''} disabled={!canEdit} onValueChange={(v) => saveLesson(unit, { teacher_id: v })}>
          <SelectTrigger className="h-8 w-44" data-testid={`tt-lesson-teacher-${tid}`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{teacherOptions.map((tc) => <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="py-2 px-2">
        <Input key={`ppw-${tid}-${resolvedPpw(unit)}`} type="number" min="0" className="h-8 w-20" disabled={!canEdit} defaultValue={resolvedPpw(unit)}
          onBlur={(e) => saveLesson(unit, { periods_per_week: Math.max(0, parseInt(e.target.value || '0', 10)) })}
          data-testid={`tt-lesson-ppw-${tid}`} />
      </td>
      <td className="py-2 px-2">
        <Input key={`mpd-${tid}-${resolvedMaxPerDay(unit)}`} type="number" min="1" className="h-8 w-16" disabled={!canEdit}
          defaultValue={resolvedMaxPerDay(unit)} placeholder="—"
          onBlur={(e) => {
            const v = e.target.value.trim();
            saveLesson(unit, { max_per_day: v === '' ? null : Math.max(1, parseInt(v, 10)) });
          }}
          data-testid={`tt-lesson-maxday-${tid}`} />
      </td>
      <td className="py-2 px-2">
        <Checkbox checked={!!l.double} disabled={!canEdit} onCheckedChange={(v) => saveLesson(unit, { double: !!v })} data-testid={`tt-lesson-double-${tid}`} />
      </td>
    </tr>
  );
}

/* --------------------- Program Time Windows (FR / PT) --------------------- */
function SubjectRulesSetup({ orgId, academicYearId, classes, config, canEdit, t }) {
  const [classId, setClassId] = useState('');
  const [pairs, setPairs] = useState([]);       // {subject_name, teacher_id, teacher_name}
  const [sections, setSections] = useState([]);  // {id, name}
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ruleType, setRuleType] = useState('sequential');
  const [pairKey, setPairKey] = useState('');    // `${subject_name}|||${teacher_id}`
  const [day, setDay] = useState('');
  const [seqTimes, setSeqTimes] = useState({});   // section_id -> time
  const [mergedTime, setMergedTime] = useState('');
  const [saving, setSaving] = useState(false);

  const activeDays = (config?.days || []).filter((d) => d.active);
  const dayPeriods = (config?.periods || []).filter((p) => !p.is_break);
  const timeOptions = (dk) => dayPeriods
    .filter((p) => !p.days || p.days.includes(dk))
    .map((p) => ({ value: p.start, label: `${p.start} (${p.label})` }));

  const loadClass = () => {
    if (!classId) { setPairs([]); setSections([]); setRules([]); return; }
    setLoading(true);
    Promise.all([
      schoolApi.ttClassSubjectTeachers({ organization_id: orgId, academic_year_id: academicYearId, class_id: classId }),
      schoolApi.listSections(orgId, classId, academicYearId),
      schoolApi.ttGetSubjectRules({ organization_id: orgId, academic_year_id: academicYearId, class_id: classId }),
    ]).then(([p, s, r]) => {
      setPairs(p?.items || []);
      setSections(s || []);
      setRules(r?.rules || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(loadClass, [orgId, academicYearId, classId]);

  const parsePair = () => {
    const [subject_name, teacher_id] = (pairKey || '').split('|||');
    const p = pairs.find((x) => x.subject_name === subject_name && (x.teacher_id || '') === (teacher_id || ''));
    return p || null;
  };

  const resetForm = () => { setPairKey(''); setDay(''); setSeqTimes({}); setMergedTime(''); };

  const saveRule = async () => {
    const p = parsePair();
    if (!p || !day) { toast.error(t('timetable.ruleNeedsSubjectDay', 'Pick a subject/teacher and a day')); return; }
    const base = { organization_id: orgId, academic_year_id: academicYearId, class_id: classId, type: ruleType,
      subject_name: p.subject_name, teacher_id: p.teacher_id, teacher_name: p.teacher_name, day };
    let body;
    if (ruleType === 'sequential') {
      const sequence = sections.map((s) => ({ section_id: s.id, time: seqTimes[s.id] })).filter((x) => x.time);
      if (!sequence.length) { toast.error(t('timetable.ruleNeedsTimes', 'Set a time for at least one section')); return; }
      body = { ...base, sequence };
    } else {
      if (!mergedTime) { toast.error(t('timetable.ruleNeedsTime', 'Pick a time slot')); return; }
      body = { ...base, time: mergedTime };
    }
    setSaving(true);
    try {
      const r = await schoolApi.ttCreateSubjectRule(body);
      setRules((prev) => [...prev, r]);
      resetForm();
      toast.success(t('timetable.ruleAdded', 'Rule added'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add rule');
    } finally { setSaving(false); }
  };

  const removeRule = async (id) => {
    try { await schoolApi.ttDeleteSubjectRule(id); setRules((prev) => prev.filter((x) => x.id !== id)); toast.success(t('timetable.ruleRemoved', 'Rule removed')); }
    catch { toast.error('Failed to remove rule'); }
  };

  const secName = (sid) => sections.find((s) => s.id === sid)?.name || sid?.slice(0, 4);
  const dayLabel = (dk) => (config?.days || []).find((d) => d.key === dk)?.label || dk;

  return (
    <Card className="p-5" data-testid="tt-subject-rules">
      <h3 className="font-semibold flex items-center gap-2 mb-1"><ListOrdered className="w-4 h-4" /> {t('timetable.subjectRules', 'Subject Rules')}</h3>
      <p className="text-xs text-muted-foreground mb-3">{t('timetable.subjectRulesHint', 'High-priority rules pulled from Lesson Requirements. Auto-Generate pins these first, then fills the rest around them.')}</p>

      <div className="flex items-center gap-2 flex-wrap mb-4 p-3 rounded-md bg-muted/40 border border-border">
        <span className="text-sm font-medium mr-1">{t('timetable.class', 'Class')}:</span>
        <Select value={classId} onValueChange={setClassId} disabled={!canEdit}>
          <SelectTrigger className="h-8 w-56" data-testid="tt-rule-class"><SelectValue placeholder={t('timetable.selectClass', 'Select class')} /></SelectTrigger>
          <SelectContent>{(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!classId ? (
        <p className="text-sm text-muted-foreground">{t('timetable.selectClassRule', 'Select a class to add rules.')}</p>
      ) : loading ? <InlineSpinner /> : (
        <>
          {/* existing rules */}
          {rules.length > 0 && (
            <div className="space-y-2 mb-4">
              {rules.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-2 p-2.5 rounded-md border border-border text-sm" data-testid={`tt-rule-${r.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={r.type === 'merged' ? 'default' : 'secondary'}>{r.type === 'merged' ? t('timetable.mergedSession', 'Merged Session') : t('timetable.sequentialBlock', 'Sequential Block')}</Badge>
                      <span className="font-medium">{r.subject_name}</span>
                      <span className="text-muted-foreground">· {r.teacher_name || '—'}</span>
                      <span className="text-muted-foreground">· {dayLabel(r.day)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {r.type === 'merged' ? `${t('timetable.allSections', 'All sections')} @ ${r.time}`
                        : (r.sequence || []).map((s) => `${secName(s.section_id)}@${s.time}`).join('  ·  ')}
                    </div>
                  </div>
                  {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => removeRule(r.id)} data-testid={`tt-rule-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              ))}
            </div>
          )}

          {canEdit && (
            <div className="rounded-md border border-border p-3 space-y-3">
              <div className="flex gap-2">
                <Button size="sm" variant={ruleType === 'sequential' ? 'default' : 'outline'} onClick={() => setRuleType('sequential')} data-testid="tt-rule-type-seq">{t('timetable.sequentialBlock', 'Sequential Block')}</Button>
                <Button size="sm" variant={ruleType === 'merged' ? 'default' : 'outline'} onClick={() => setRuleType('merged')} data-testid="tt-rule-type-merged">{t('timetable.mergedSession', 'Merged Session')}</Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Select value={pairKey} onValueChange={setPairKey}>
                  <SelectTrigger className="h-8 w-64" data-testid="tt-rule-pair"><SelectValue placeholder={t('timetable.subjectTeacher', 'Subject · Teacher')} /></SelectTrigger>
                  <SelectContent>
                    {pairs.map((p) => <SelectItem key={`${p.subject_name}|||${p.teacher_id}`} value={`${p.subject_name}|||${p.teacher_id || ''}`}>{p.subject_name} · {p.teacher_name || '—'}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={day} onValueChange={(v) => { setDay(v); setSeqTimes({}); setMergedTime(''); }}>
                  <SelectTrigger className="h-8 w-40" data-testid="tt-rule-day"><SelectValue placeholder={t('timetable.day', 'Day')} /></SelectTrigger>
                  <SelectContent>{activeDays.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
                {ruleType === 'merged' && (
                  <Select value={mergedTime} onValueChange={setMergedTime} disabled={!day}>
                    <SelectTrigger className="h-8 w-44" data-testid="tt-rule-merged-time"><SelectValue placeholder={t('timetable.timeSlot', 'Time slot')} /></SelectTrigger>
                    <SelectContent>{timeOptions(day).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>

              {ruleType === 'sequential' && day && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {sections.map((s) => (
                    <div key={s.id} className="flex items-center gap-2">
                      <span className="text-sm w-16 shrink-0">{s.name}</span>
                      <Select value={seqTimes[s.id] || ''} onValueChange={(v) => setSeqTimes((p) => ({ ...p, [s.id]: v === '__none__' ? undefined : v }))}>
                        <SelectTrigger className="h-8" data-testid={`tt-rule-seq-${s.id}`}><SelectValue placeholder={t('timetable.time', 'Time')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t('timetable.skip', '— skip —')}</SelectItem>
                          {timeOptions(day).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end">
                <Button size="sm" onClick={saveRule} disabled={saving} className="gap-2" data-testid="tt-rule-save">
                  {saving ? <InlineSpinner /> : <Plus className="w-4 h-4" />} {t('timetable.addRule', 'Add rule')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}


function MergedSubjectsSetup({ orgId, academicYearId, classes, canEdit, t }) {
  const [classId, setClassId] = useState('');
  const [names, setNames] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load the chosen class's subjects + its saved merged rule.
  useEffect(() => {
    if (!classId) { setNames([]); setSelected([]); return; }
    let alive = true;
    setLoading(true);
    Promise.all([
      schoolApi.ttSubjectNames({ organization_id: orgId, academic_year_id: academicYearId, class_id: classId }),
      schoolApi.ttGetMergedSubjects({ organization_id: orgId, academic_year_id: academicYearId, class_id: classId }),
    ]).then(([n, m]) => {
      if (!alive) return;
      setNames(n?.names || []);
      setSelected(m?.subjects || []);
    }).catch(() => { if (alive) { setNames([]); setSelected([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgId, academicYearId, classId]);

  const isSelected = (nm) => selected.some((s) => s.toLowerCase() === nm.toLowerCase());

  const persist = async (next) => {
    setSaving(true);
    try {
      const r = await schoolApi.ttSaveMergedSubjects({ organization_id: orgId, academic_year_id: academicYearId, class_id: classId, subjects: next });
      setSelected(r?.subjects || next);
      toast.success(t('timetable.mergedSaved', 'Merged subjects updated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save merged subjects');
    } finally { setSaving(false); }
  };

  const toggle = (nm) => {
    if (!canEdit || !classId) return;
    const next = isSelected(nm)
      ? selected.filter((s) => s.toLowerCase() !== nm.toLowerCase())
      : [...selected, nm];
    setSelected(next);
    persist(next);
  };

  const selectedClass = (classes || []).find((c) => c.id === classId);

  return (
    <Card className="p-5" data-testid="tt-merged-subjects">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> {t('timetable.mergedSubjects', 'Merged Subjects')}</h3>
        {saving && <InlineSpinner />}
      </div>
      <p className="text-xs text-muted-foreground mb-3">{t('timetable.mergedSubjectsHint', 'Pick a class, then tick the subjects that must run at the SAME day & period across all sections of THAT class (e.g. Sport with one shared teacher). Rules are saved per class, so one grade never forces a merge for another.')}</p>

      {/* class selector */}
      <div className="flex items-center gap-2 flex-wrap mb-4 p-3 rounded-md bg-muted/40 border border-border">
        <span className="text-sm font-medium mr-1">{t('timetable.class', 'Class')}:</span>
        <Select value={classId} onValueChange={setClassId} disabled={!canEdit}>
          <SelectTrigger className="h-8 w-56" data-testid="tt-merge-class"><SelectValue placeholder={t('timetable.selectClass', 'Select class')} /></SelectTrigger>
          <SelectContent>
            {(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!classId ? (
        <p className="text-sm text-muted-foreground" data-testid="tt-merge-empty">{t('timetable.selectClassMerge', 'Select a class to configure its merged subjects.')}</p>
      ) : loading ? <InlineSpinner /> : (
        names.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('timetable.noSubjectsClass', 'No subjects found for this class. Add subjects to it first.')}</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="tt-merged-list">
              {names.map((nm) => {
                const on = isSelected(nm);
                return (
                  <div key={nm} role="button" tabIndex={canEdit ? 0 : -1}
                    onClick={() => toggle(nm)}
                    onKeyDown={(e) => { if (canEdit && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(nm); } }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors select-none ${on ? 'bg-primary/10 border-primary text-primary' : 'border-border hover:bg-accent'} ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`tt-merge-toggle-${nm}`}>
                    <Checkbox checked={on} className="pointer-events-none" tabIndex={-1} />
                    <span className="font-medium">{nm}</span>
                  </div>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">{t('timetable.mergedActiveClass', 'Merged for')} {selectedClass?.name || ''}:</span>
                {selected.map((s) => <Badge key={s} variant="secondary" data-testid={`tt-merge-badge-${s}`}>{s}</Badge>)}
              </div>
            )}
          </>
        )
      )}
    </Card>
  );
}


function CyclesPairingsSetup({ orgId, academicYearId, classes, canEdit, t }) {
  const [cycles, setCycles] = useState([
    { cycle: 1, name: 'Cycle 1', class_ids: [] },
    { cycle: 2, name: 'Cycle 2', class_ids: [] },
    { cycle: 3, name: 'Cycle 3', class_ids: [] },
  ]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [secByClass, setSecByClass] = useState({}); // class_id -> [{id,name}]
  const [pairs, setPairs] = useState([]);
  const [pairA, setPairA] = useState({}); // cycleNum -> section_id
  const [pairB, setPairB] = useState({});
  const [pairing, setPairing] = useState(false);

  const classNameOf = (cid) => (classes || []).find((c) => c.id === cid)?.name || cid?.slice(0, 6);

  // Program (FR/PT) detection: prefer bulletin_format, fall back to the class name suffix.
  const programOfClass = (cid) => {
    const cl = (classes || []).find((c) => c.id === cid);
    const bf = cl?.bulletin_format;
    if (bf === 'primary_fr') return 'french';
    if (bf === 'primary_pt') return 'portuguese';
    const nm = (cl?.name || '').toUpperCase();
    if (/\bFR\b/.test(nm) || nm.endsWith(' FR') || nm.endsWith('/FR')) return 'french';
    if (/\bPT\b/.test(nm) || nm.endsWith(' PT') || nm.endsWith('/PT')) return 'portuguese';
    return 'other';
  };

  const loadAll = async () => {
    if (!orgId || !academicYearId) return;
    setLoading(true);
    try {
      const [cyc, prs] = await Promise.all([
        schoolApi.ttGetCycles({ organization_id: orgId, academic_year_id: academicYearId }),
        schoolApi.ttListPairs({ organization_id: orgId, academic_year_id: academicYearId }),
      ]);
      const base = [1, 2, 3].map((n) => {
        const found = (cyc?.cycles || []).find((c) => c.cycle === n);
        return { cycle: n, name: found?.name || `Cycle ${n}`, class_ids: found?.class_ids || [] };
      });
      setCycles(base);
      setPairs(prs?.items || []);
      // fetch sections for all classes (for pairing selects)
      const entries = await Promise.all((classes || []).map(async (c) => {
        try {
          const s = await schoolApi.listSections(orgId, c.id, academicYearId);
          return [c.id, (s || []).map((x) => ({ id: x.id, name: x.name }))];
        } catch { return [c.id, []]; }
      }));
      setSecByClass(Object.fromEntries(entries));
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [orgId, academicYearId, (classes || []).length]);

  const cycleOfClass = (cid) => cycles.find((c) => c.class_ids.includes(cid))?.cycle || null;
  const toggleClass = (cid, cycleNum) => {
    if (!canEdit) return;
    setCycles((prev) => prev.map((c) => {
      const without = c.class_ids.filter((x) => x !== cid);
      if (c.cycle === cycleNum) {
        const has = c.class_ids.includes(cid);
        return { ...c, class_ids: has ? without : [...without, cid] };
      }
      return { ...c, class_ids: without }; // ensure a class lives in only one cycle
    }));
  };

  const saveCycles = async () => {
    setSaving(true);
    try {
      const res = await schoolApi.ttSaveCycles({ organization_id: orgId, academic_year_id: academicYearId, cycles });
      setCycles(res?.cycles || cycles);
      toast.success(t('timetable.cyclesSaved', 'Cycles saved'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save cycles'); }
    finally { setSaving(false); }
  };

  const sectionsForCycle = (cycleNum) => {
    const c = cycles.find((x) => x.cycle === cycleNum);
    const out = [];
    (c?.class_ids || []).forEach((cid) => {
      (secByClass[cid] || []).forEach((s) => out.push({ id: s.id, label: `${classNameOf(cid)} · ${s.name}` }));
    });
    return out;
  };
  // Sections for one side of a pairing: 'fr' = French sections, 'pt' = Portuguese
  // sections. Classes with no FR/PT program appear on both sides.
  const sectionsForCycleSide = (cycleNum, side) => {
    const c = cycles.find((x) => x.cycle === cycleNum);
    const out = [];
    (c?.class_ids || []).forEach((cid) => {
      const prog = programOfClass(cid);
      const match = side === 'fr'
        ? (prog === 'french' || prog === 'other')
        : (prog === 'portuguese' || prog === 'other');
      if (!match) return;
      (secByClass[cid] || []).forEach((s) => out.push({ id: s.id, label: `${classNameOf(cid)} · ${s.name}` }));
    });
    return out;
  };
  const pairsForCycle = (cycleNum) => {
    const c = cycles.find((x) => x.cycle === cycleNum);
    const ids = new Set(c?.class_ids || []);
    return pairs.filter((p) => ids.has(p.class_id) || ids.has(p.paired_class_id));
  };

  const doPair = async (cycleNum) => {
    const a = pairA[cycleNum]; const b = pairB[cycleNum];
    if (!a || !b || a === b) { toast.error(t('timetable.pickTwoSections', 'Pick two different sections to pair')); return; }
    setPairing(true);
    try {
      await schoolApi.ttSetPair({ organization_id: orgId, academic_year_id: academicYearId, section_id: a, paired_section_id: b });
      setPairA((p) => ({ ...p, [cycleNum]: '' })); setPairB((p) => ({ ...p, [cycleNum]: '' }));
      const prs = await schoolApi.ttListPairs({ organization_id: orgId, academic_year_id: academicYearId });
      setPairs(prs?.items || []);
      toast.success(t('timetable.paired', 'Sections paired'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to pair'); }
    finally { setPairing(false); }
  };
  const doUnpair = async (section_id) => {
    try {
      await schoolApi.ttSetPair({ organization_id: orgId, academic_year_id: academicYearId, section_id, paired_section_id: null });
      const prs = await schoolApi.ttListPairs({ organization_id: orgId, academic_year_id: academicYearId });
      setPairs(prs?.items || []);
      toast.success(t('timetable.unpaired', 'Pairing removed'));
    } catch (e) { toast.error('Failed to unpair'); }
  };

  return (
    <Card className="p-5" data-testid="tt-cycles-setup">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="font-semibold mb-1 flex items-center gap-2"><ListOrdered className="w-4 h-4" /> {t('timetable.cyclesPairings', 'Cycles & Pairings')}</h3>
          <p className="text-xs text-muted-foreground mb-3">{t('timetable.cyclesHint', 'Group classes into three persistent cycles, then pair sections that share the same pupils. Auto-Generate can then run one cycle at a time without touching the others.')}</p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={saveCycles} disabled={saving || loading} className="gap-2 shrink-0" data-testid="tt-cycles-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('common.save', 'Save')}
          </Button>
        )}
      </div>

      {loading ? <InlineSpinner /> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {cycles.map((c) => (
            <div key={c.cycle} className="rounded-lg border border-border p-3 space-y-3" data-testid={`tt-cycle-${c.cycle}`}>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">{c.name}</Badge>
                <span className="text-xs text-muted-foreground">{c.class_ids.length} {t('timetable.classesWord', 'classes')}</span>
              </div>
              {/* class assignment */}
              <div className="max-h-52 overflow-auto space-y-1 pr-1">
                {(classes || []).map((cl) => {
                  const owner = cycleOfClass(cl.id);
                  const mine = owner === c.cycle;
                  const takenElsewhere = owner && !mine;
                  return (
                    <label key={cl.id} className={`flex items-center gap-2 text-sm rounded px-1.5 py-1 ${takenElsewhere ? 'opacity-40' : 'hover:bg-accent cursor-pointer'}`}>
                      <Checkbox checked={mine} disabled={!canEdit || takenElsewhere} onCheckedChange={() => toggleClass(cl.id, c.cycle)} data-testid={`tt-cycle-${c.cycle}-class-${cl.id}`} />
                      <span className="truncate">{cl.name}</span>
                    </label>
                  );
                })}
              </div>

              {/* pairings within this cycle */}
              <div className="border-t border-border pt-2 space-y-2">
                <p className="text-xs font-medium flex items-center gap-1.5"><ArrowLeftRight className="w-3.5 h-3.5" /> {t('timetable.pairings', 'Pairings')}</p>
                {canEdit && (() => {
                  const frList = sectionsForCycleSide(c.cycle, 'fr').filter((s) => s.id !== pairB[c.cycle]);
                  const ptList = sectionsForCycleSide(c.cycle, 'pt').filter((s) => s.id !== pairA[c.cycle]);
                  return (
                  <>
                  <div className="flex items-center gap-1.5">
                    <Select value={pairA[c.cycle] || ''} onValueChange={(v) => setPairA((p) => ({ ...p, [c.cycle]: v }))}>
                      <SelectTrigger className="h-8 text-xs flex-1" data-testid={`tt-pair-a-${c.cycle}`}><SelectValue placeholder={t('timetable.frSection', 'French section')} /></SelectTrigger>
                      <SelectContent>{frList.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <ArrowLeftRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <Select value={pairB[c.cycle] || ''} onValueChange={(v) => setPairB((p) => ({ ...p, [c.cycle]: v }))}>
                      <SelectTrigger className="h-8 text-xs flex-1" data-testid={`tt-pair-b-${c.cycle}`}><SelectValue placeholder={t('timetable.ptSection', 'Portuguese section')} /></SelectTrigger>
                      <SelectContent>{ptList.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="icon" variant="outline" className="h-8 w-8 shrink-0" onClick={() => doPair(c.cycle)} disabled={pairing} data-testid={`tt-pair-add-${c.cycle}`}><Plus className="w-4 h-4" /></Button>
                  </div>
                  {(frList.length === 0 || ptList.length === 0) && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-500" data-testid={`tt-pair-hint-${c.cycle}`}>
                      {frList.length === 0
                        ? t('timetable.noFrInCycle', 'Add a French (FR) class to this cycle to pair.')
                        : t('timetable.noPtInCycle', 'Add a Portuguese (PT) class to this cycle to pair.')}
                    </p>
                  )}
                  </>
                  );
                })()}
                <div className="space-y-1">
                  {pairsForCycle(c.cycle).length === 0 && <p className="text-[11px] text-muted-foreground">{t('timetable.noPairings', 'No pairings yet')}</p>}
                  {pairsForCycle(c.cycle).map((p) => (
                    <div key={p.section_id} className="flex items-center justify-between gap-2 text-xs bg-muted/40 rounded px-2 py-1" data-testid="tt-pair-row">
                      <span className="truncate">{p.class_name} · {p.section_name} ↔ {p.paired_class_name} · {p.paired_section_name}</span>
                      {canEdit && <button type="button" onClick={() => doUnpair(p.section_id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid="tt-pair-remove"><X className="w-3.5 h-3.5" /></button>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}



// Preset reasons for blocking a teacher slot. Free text is offered via "Other".
const AVAIL_REASONS = ['Personal commitment', 'Medical', 'Administrative duty'];

// A single day/period cell in the Teacher Availability editor. Blocked cells show a
// grey diagonal stripe + lock + the reason; clicking opens a reason picker (preset or
// free text) and lets you unblock. A reason is REQUIRED to block a slot.
function AvailabilityCell({ day, seq, blocked, reason, canEdit, onBlock, onClear, t }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState('');
  const [free, setFree] = useState('');
  useEffect(() => {
    if (open) {
      const pm = AVAIL_REASONS.includes(reason) ? reason : (blocked ? '__other__' : '');
      setPreset(pm);
      setFree(AVAIL_REASONS.includes(reason) ? '' : (reason || ''));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const effectiveReason = preset === '__other__' || preset === '' ? free.trim() : preset;
  const apply = () => { if (!effectiveReason) return; onBlock(effectiveReason); setOpen(false); };

  const stripe = { backgroundImage: 'repeating-linear-gradient(45deg, rgba(100,116,139,0.35) 0, rgba(100,116,139,0.35) 5px, rgba(100,116,139,0.12) 5px, rgba(100,116,139,0.12) 10px)' };
  const btn = (
    <button type="button" disabled={!canEdit} onClick={() => canEdit && setOpen(true)}
      title={blocked ? `${t('timetable.blocked', 'Blocked')}: ${reason || t('timetable.noReason', 'no reason given')}` : ''}
      style={blocked ? stripe : undefined}
      className={`w-full h-9 min-w-[76px] px-1 text-[10px] transition-colors inline-flex items-center justify-center gap-1 ${blocked ? 'text-slate-700 dark:text-slate-200 font-medium' : 'hover:bg-accent text-muted-foreground'} ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
      data-testid={`tt-availcell-${day}-${seq}`}>
      {blocked ? (<><Lock className="w-3 h-3 shrink-0" /><span className="truncate max-w-[72px]">{reason || t('timetable.blocked', 'Blocked')}</span></>) : ''}
    </button>
  );
  if (!canEdit) return btn;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{btn}</PopoverTrigger>
      <PopoverContent className="w-64 space-y-2" data-testid={`tt-availpop-${day}-${seq}`}>
        <p className="text-xs font-medium">{t('timetable.blockReason', 'Reason for unavailability')}</p>
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="h-8" data-testid={`tt-availreason-${day}-${seq}`}><SelectValue placeholder={t('timetable.selectReason', 'Select a reason')} /></SelectTrigger>
          <SelectContent>
            {AVAIL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            <SelectItem value="__other__">{t('timetable.otherReason', 'Other (free text)')}</SelectItem>
          </SelectContent>
        </Select>
        {preset === '__other__' && (
          <Input className="h-8" value={free} onChange={(e) => setFree(e.target.value)}
            placeholder={t('timetable.reasonPlaceholder', 'Type a reason')} data-testid={`tt-availfree-${day}-${seq}`} />
        )}
        <div className="flex items-center justify-between pt-1">
          {blocked
            ? <Button size="sm" variant="ghost" className="text-destructive gap-1" onClick={() => { onClear(); setOpen(false); }} data-testid={`tt-availunblock-${day}-${seq}`}><X className="w-3.5 h-3.5" /> {t('timetable.unblock', 'Unblock')}</Button>
            : <span />}
          <Button size="sm" className="gap-1" disabled={!effectiveReason} onClick={apply} data-testid={`tt-availblock-${day}-${seq}`}>
            <Lock className="w-3.5 h-3.5" /> {blocked ? t('common.update', 'Update') : t('timetable.block', 'Block')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Setup panel: list every teacher (with a blocked-count badge) and open a day×period
// editor to block slots with a required reason. Saved per teacher per academic year.
function TeacherAvailabilitySetup({ orgId, academicYearId, teachers, config, activeDays, canEdit, t }) {
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editTeacher, setEditTeacher] = useState(null);
  const [blocks, setBlocks] = useState({}); // "day:seq" -> reason
  const [maxPerDay, setMaxPerDay] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const teachPeriods = ((config?.periods) || []).filter((p) => !p.is_break);

  const loadSummary = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const r = await schoolApi.ttGetAvailabilityAll({ organization_id: orgId, academic_year_id: academicYearId });
      const map = {};
      (r?.teachers || []).forEach((x) => { map[x.teacher_id] = x.blocked_count; });
      setSummary(map);
    } catch { setSummary({}); }
    finally { setLoading(false); }
  }, [orgId, academicYearId]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const openEditor = async (teacher) => {
    setEditTeacher(teacher); setOpen(true); setEditLoading(true);
    try {
      const av = await schoolApi.ttGetAvailability({ organization_id: orgId, teacher_id: teacher.id, academic_year_id: academicYearId });
      const b = {};
      (av?.unavailable || []).forEach((u) => { b[`${u.day}:${u.period_seq}`] = u.reason || ''; });
      setBlocks(b);
      setMaxPerDay(av?.max_per_day != null ? String(av.max_per_day) : '');
    } catch { toast.error(t('timetable.availLoadFail', 'Failed to load availability')); }
    finally { setEditLoading(false); }
  };

  const setBlock = (day, seq, reason) => setBlocks((p) => ({ ...p, [`${day}:${seq}`]: reason }));
  const clearBlock = (day, seq) => setBlocks((p) => { const n = { ...p }; delete n[`${day}:${seq}`]; return n; });

  const save = async () => {
    if (!editTeacher) return;
    setSaving(true);
    try {
      const unavailable = Object.entries(blocks).map(([k, reason]) => {
        const [day, seq] = k.split(':'); return { day, period_seq: Number(seq), reason };
      });
      await schoolApi.ttSaveAvailability({
        organization_id: orgId, academic_year_id: academicYearId, teacher_id: editTeacher.id,
        unavailable, max_per_day: maxPerDay === '' ? null : Number(maxPerDay),
      });
      toast.success(t('timetable.availabilitySaved', 'Availability saved — regenerate to apply'));
      setOpen(false); loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save availability'); }
    finally { setSaving(false); }
  };

  const blockCount = Object.keys(blocks).length;

  return (
    <Card className="p-5" data-testid="tt-availability-setup">
      <div className="mb-4">
        <h3 className="font-semibold flex items-center gap-2"><CalendarClock className="w-4 h-4" /> {t('timetable.teacherAvailability', 'Teacher Availability')}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{t('timetable.availabilitySetupHint', 'Block the day/period slots when a teacher is unavailable (each block needs a reason). Blocked slots are hard constraints the auto-generator and manual placement always respect.')}</p>
      </div>
      {loading ? <InlineSpinner /> : (teachers || []).length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('timetable.noTeachersFound', 'No teachers found for this year.')}</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {(teachers || []).map((tc) => {
            const count = summary[tc.id] || 0;
            return (
              <div key={tc.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2" data-testid={`tt-avail-teacher-${tc.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{tc.name}</p>
                  {count > 0
                    ? <Badge variant="secondary" className="mt-1 gap-1" data-testid={`tt-avail-count-${tc.id}`}><Lock className="w-3 h-3" /> {count} {t('timetable.blockedSlots', 'blocked')}</Badge>
                    : <span className="text-[11px] text-muted-foreground">{t('timetable.noBlocks', 'No blocks')}</span>}
                </div>
                <Button size="sm" variant="outline" className="gap-1 shrink-0" onClick={() => openEditor({ id: tc.id, name: tc.name })} data-testid={`tt-avail-edit-${tc.id}`}>
                  <CalendarClock className="w-3.5 h-3.5" /> {canEdit ? t('common.edit', 'Edit') : t('common.view', 'View')}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!saving) setOpen(o); }}>
        <DialogContent className="max-w-3xl" data-testid="tt-avail-setup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarClock className="w-4 h-4" /> {t('timetable.teacherAvailability', 'Teacher Availability')}{editTeacher?.name ? ` — ${editTeacher.name}` : ''}</DialogTitle>
          </DialogHeader>
          {editLoading ? <InlineSpinner /> : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{t('timetable.availGridHint', 'Click a slot to block it and choose a reason. Click a blocked slot to change the reason or unblock it.')}</p>
              <div className="overflow-x-auto">
                <table className="border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="border border-border bg-muted/50 p-1.5 text-left">{t('timetable.period', 'Period')}</th>
                      {activeDays.map((d) => <th key={d.key} className="border border-border bg-muted/50 p-1.5">{d.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {teachPeriods.map((p) => (
                      <tr key={p.seq}>
                        <td className="border border-border p-1.5 whitespace-nowrap text-muted-foreground">{p.label || `P${p.seq}`}</td>
                        {activeDays.map((d) => {
                          const key = `${d.key}:${p.seq}`;
                          const reason = blocks[key];
                          const isBlocked = reason !== undefined;
                          return (
                            <td key={d.key} className="border border-border p-0">
                              <AvailabilityCell day={d.key} seq={p.seq} blocked={isBlocked} reason={reason}
                                canEdit={canEdit} onBlock={(r) => setBlock(d.key, p.seq, r)} onClear={() => clearBlock(d.key, p.seq)} t={t} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">{t('timetable.maxPerDay', 'Max periods/day')}</Label>
                  <Input type="number" min="0" className="h-8 w-24" value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.value)} placeholder={t('timetable.noLimit', 'No limit')} disabled={!canEdit} data-testid="tt-avail-setup-max" />
                </div>
                <span className="text-[11px] text-muted-foreground" data-testid="tt-avail-setup-total">{blockCount} {t('timetable.blockedSlots', 'blocked')}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} data-testid="tt-avail-setup-cancel">{t('common.cancel', 'Cancel')}</Button>
            {canEdit && <Button onClick={save} disabled={saving || editLoading} className="gap-2" data-testid="tt-avail-setup-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('common.save', 'Save')}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function ProgramWindowsSetup({ orgId, academicYearId, classes, config, canEdit, t }) {
  const [pw, setPw] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingScope, setLoadingScope] = useState(false);
  const [isOverride, setIsOverride] = useState(false);
  const [scopeClassId, setScopeClassId] = useState('default'); // 'default' | classId
  const [scopeSectionId, setScopeSectionId] = useState('');
  const [scopeSections, setScopeSections] = useState([]);
  const dayLabels = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
  const activeDayKeys = (config?.days || []).filter((d) => d.active).map((d) => d.key);

  // sections for the chosen scope class
  useEffect(() => {
    if (scopeClassId === 'default') { setScopeSections([]); setScopeSectionId(''); return; }
    schoolApi.listSections(orgId, scopeClassId, academicYearId).then((s) => setScopeSections(s || [])).catch(() => setScopeSections([]));
    setScopeSectionId('');
  }, [scopeClassId, orgId, academicYearId]);

  // the section_id we're scoping to (null = Default)
  const scopeSectionKey = scopeClassId === 'default' ? null : (scopeSectionId || null);

  // load the rule for the current scope
  useEffect(() => {
    let alive = true;
    // when a class is picked but no section yet, wait for a section choice
    if (scopeClassId !== 'default' && !scopeSectionId) { setPw(null); return; }
    setLoadingScope(true);
    schoolApi.ttGetProgramWindows({ organization_id: orgId, academic_year_id: academicYearId, section_id: scopeSectionKey || undefined })
      .then((r) => { if (alive) { setPw({ enabled: r.enabled !== false, days: r.days || {} }); setIsOverride(!!r.is_override); } })
      .catch(() => { if (alive) { setPw({ enabled: true, days: {} }); setIsOverride(false); } })
      .finally(() => { if (alive) setLoadingScope(false); });
    return () => { alive = false; };
  }, [orgId, academicYearId, scopeClassId, scopeSectionId]);

  const updateBlock = (day, idx, field, value) => setPw((s) => {
    const days = { ...s.days };
    days[day] = (days[day] || []).map((b, i) => (i === idx ? { ...b, [field]: value } : b));
    return { ...s, days };
  });
  const addBlock = (day) => setPw((s) => {
    const days = { ...s.days };
    days[day] = [...(days[day] || []), { program: 'FR', from: '07:50', to: '11:45' }];
    return { ...s, days };
  });
  const removeBlock = (day, idx) => setPw((s) => {
    const days = { ...s.days };
    days[day] = (days[day] || []).filter((_, i) => i !== idx);
    return { ...s, days };
  });

  const save = async () => {
    setSaving(true);
    try {
      const saved = await schoolApi.ttSaveProgramWindows({ organization_id: orgId, academic_year_id: academicYearId, section_id: scopeSectionKey || undefined, enabled: pw.enabled, days: pw.days });
      setPw({ enabled: saved.enabled !== false, days: saved.days || {} });
      setIsOverride(!!saved.is_override);
      toast.success(scopeSectionKey ? t('timetable.windowsSavedSection', 'Saved for this class/section') : t('timetable.windowsSaved', 'Default program windows saved'));
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const useDefault = async () => {
    if (!scopeSectionKey) return;
    setSaving(true);
    try {
      await schoolApi.ttSaveProgramWindows({ organization_id: orgId, academic_year_id: academicYearId, section_id: scopeSectionKey, clear: true });
      const r = await schoolApi.ttGetProgramWindows({ organization_id: orgId, academic_year_id: academicYearId, section_id: scopeSectionKey });
      setPw({ enabled: r.enabled !== false, days: r.days || {} });
      setIsOverride(!!r.is_override);
      toast.success(t('timetable.windowsCleared', 'Now using the Default rule'));
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const showEditor = pw && !(scopeClassId !== 'default' && !scopeSectionId);

  return (
    <Card className="p-5" data-testid="tt-program-windows">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="font-semibold flex items-center gap-2"><CalendarClock className="w-4 h-4" /> {t('timetable.programWindows', 'Program Time Windows (FR / PT)')}</h3>
        {canEdit && showEditor && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={pw.enabled} onCheckedChange={(v) => setPw((s) => ({ ...s, enabled: v }))} data-testid="tt-windows-enabled" /> {t('timetable.enabled', 'Enabled')}</label>
            <Button onClick={save} disabled={saving} size="sm" className="gap-2" data-testid="tt-windows-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('common.save', 'Save')}</Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-3">{t('timetable.programWindowsHint', 'Auto-Generate keeps Français subjects inside the FR blocks and Português subjects inside the PT blocks for each day. Programs are detected from each class name (…FR / …PT). Paired sections share the same rule.')}</p>

      {/* scope selector */}
      <div className="flex items-center gap-2 flex-wrap mb-4 p-3 rounded-md bg-muted/40 border border-border">
        <span className="text-sm font-medium mr-1">{t('timetable.appliesTo', 'Applies to')}:</span>
        <Select value={scopeClassId} onValueChange={setScopeClassId} disabled={!canEdit}>
          <SelectTrigger className="h-8 w-52" data-testid="tt-windows-scope-class"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">{t('timetable.defaultAllClasses', 'Default (all classes)')}</SelectItem>
            {(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {scopeClassId !== 'default' && (
          <Select value={scopeSectionId} onValueChange={setScopeSectionId} disabled={!canEdit || !scopeSections.length}>
            <SelectTrigger className="h-8 w-40" data-testid="tt-windows-scope-section"><SelectValue placeholder={t('timetable.selectSection', 'Section')} /></SelectTrigger>
            <SelectContent>{scopeSections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {showEditor && scopeSectionKey && (
          <Badge variant={isOverride ? 'secondary' : 'outline'} data-testid="tt-windows-scope-badge">
            {isOverride ? t('timetable.customRule', 'Custom rule') : t('timetable.inheritsDefault', 'Inherits Default')}
          </Badge>
        )}
        {canEdit && showEditor && scopeSectionKey && isOverride && (
          <Button onClick={useDefault} disabled={saving} size="sm" variant="outline" className="h-8 gap-1" data-testid="tt-windows-use-default"><RotateCcw className="w-3.5 h-3.5" /> {t('timetable.useDefault', 'Use Default')}</Button>
        )}
      </div>

      {loadingScope ? <InlineSpinner /> : !showEditor ? (
        <p className="text-sm text-muted-foreground py-2" data-testid="tt-windows-pick-section">{t('timetable.pickSectionForRule', 'Pick a section to view or customise its rule.')}</p>
      ) : (
        <div className="space-y-3">
          {activeDayKeys.map((day) => (
            <div key={day} className="flex items-start gap-3 flex-wrap border-b border-border/60 pb-3 last:border-0" data-testid={`tt-window-day-${day}`}>
              <div className="w-24 text-sm font-medium pt-1.5">{t(`days.${day}`, dayLabels[day] || day)}</div>
              <div className="flex-1 space-y-2">
                {(pw.days[day] || []).map((blk, idx) => (
                  <div key={idx} className="flex items-center gap-2 flex-wrap">
                    <Select value={blk.program} onValueChange={(v) => updateBlock(day, idx, 'program', v)} disabled={!canEdit}>
                      <SelectTrigger className="h-8 w-36" data-testid={`tt-window-${day}-${idx}-prog`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FR">{t('timetable.french', 'Français (FR)')}</SelectItem>
                        <SelectItem value="PT">{t('timetable.portuguese', 'Português (PT)')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="time" className="h-8 w-28" value={blk.from || ''} disabled={!canEdit} onChange={(e) => updateBlock(day, idx, 'from', e.target.value)} data-testid={`tt-window-${day}-${idx}-from`} />
                    <span className="text-muted-foreground">–</span>
                    <Input type="time" className="h-8 w-28" value={blk.to || ''} disabled={!canEdit} onChange={(e) => updateBlock(day, idx, 'to', e.target.value)} data-testid={`tt-window-${day}-${idx}-to`} />
                    {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeBlock(day, idx)} data-testid={`tt-window-${day}-${idx}-remove`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                ))}
                {canEdit && <Button onClick={() => addBlock(day)} size="sm" variant="outline" className="gap-1 h-7" data-testid={`tt-window-${day}-add`}><Plus className="w-3 h-3" /> {t('timetable.addBlock', 'Add block')}</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


/* ----------------------------- Timetable Grid ----------------------------- */
function TimetableGrid({ orgId, academicYearId, classes, teachers, config, activeDays, canEdit, t, onGoToSetup }) {
  const { currentOrg } = useAuth();
  const { selectedYear } = useAcademicYear();
  const schoolName = currentOrg?.name || 'Escola Libanesa Internacional';
  const logoUrl = currentOrg?.logo_url
    ? (currentOrg.logo_url.startsWith('http') ? currentOrg.logo_url : `${process.env.REACT_APP_BACKEND_URL}${currentOrg.logo_url}`)
    : '/eli-crest.png';
  // Pre-decode the logo into a base64 data URL so the PDF header renders reliably:
  // this removes both the html2canvas timing race AND any cross-origin (CORS/tainted
  // canvas) issue with a remote/S3 logo. Falls back to the raw URL if inlining fails.
  const [logoData, setLogoData] = useState(null);
  useEffect(() => {
    let alive = true;
    setLogoData(null);
    (async () => {
      try {
        const resp = await fetch(logoUrl, { mode: 'cors' });
        if (!resp.ok) throw new Error('logo fetch failed');
        const blob = await resp.blob();
        const reader = new FileReader();
        reader.onloadend = () => { if (alive) setLogoData(reader.result); };
        reader.readAsDataURL(blob);
      } catch (_e) {
        if (alive) setLogoData(null); // keep raw URL fallback
      }
    })();
    return () => { alive = false; };
  }, [logoUrl]);
  const printLogo = logoData || logoUrl;
  const yearName = selectedYear?.name || '';
  const [mode, setMode] = useState('section'); // section | teacher
  const [classId, setClassId] = useState('');
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [entries, setEntries] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0); // background solve progress 0-100
  const [genStep, setGenStep] = useState(''); // background solve step label
  const [genJobId, setGenJobId] = useState(null); // active background job id
  const genCancelRef = useRef(false); // set when user cancels polling
  const [unplaced, setUnplaced] = useState([]);
  const [conflictReport, setConflictReport] = useState([]);
  const [editCell, setEditCell] = useState(null); // {day, seq, entry}
  const [placeLessonId, setPlaceLessonId] = useState(null); // Manual Placement Helper: selected missing lesson id
  const [legalSet, setLegalSet] = useState(new Set()); // "day:seq" cells legal for placeLessonId
  const [pairedBlockedSet, setPairedBlockedSet] = useState(new Set()); // "day:seq" cells blocked by the paired class
  const [pairedBlockLabel, setPairedBlockLabel] = useState('');
  const [legalLoading, setLegalLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [teacherAdd, setTeacherAdd] = useState(null); // {day, seq} in teacher view
  const [clearTeacherOpen, setClearTeacherOpen] = useState(false);
  const [clearingTeacher, setClearingTeacher] = useState(false);
  const [swapMode, setSwapMode] = useState(false);
  const [swapFrom, setSwapFrom] = useState(null); // {day, seq}
  const [swapping, setSwapping] = useState(false);
  const [workload, setWorkload] = useState(null);
  const [workloadOpen, setWorkloadOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [clearScope, setClearScope] = useState('year'); // 'year' | 'class'
  const [pairClassId, setPairClassId] = useState('none');
  const [pairSectionId, setPairSectionId] = useState('');
  const [pairSections, setPairSections] = useState([]);
  const [savedPair, setSavedPair] = useState(null); // {paired_section_id, paired_class_name, paired_section_name}
  const [respectWindows, setRespectWindows] = useState(true); // FR/PT program time-windows
  const [genAll, setGenAll] = useState(true); // whole-school single pass (only locked stay fixed)
  const [genCycle, setGenCycle] = useState('all'); // 'all' | '1' | '2' | '3'
  const [cyclesList, setCyclesList] = useState([]);
  const [solveTime, setSolveTime] = useState(20); // Auto-Generate search budget (seconds)
  const [availOpen, setAvailOpen] = useState(false);
  const [availTeacher, setAvailTeacher] = useState(null); // {id, name}
  const [availBlocked, setAvailBlocked] = useState(new Set()); // "day:seq"
  const [teacherBlocks, setTeacherBlocks] = useState({}); // "day:seq" -> reason (By-Teacher grid overlay)
  const [availMaxPerDay, setAvailMaxPerDay] = useState('');
  const [availLoading, setAvailLoading] = useState(false);
  const [availSaving, setAvailSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const gridRef = useRef(null);
  const [singleSheet, setSingleSheet] = useState(null); // branded printable sheet for single Export PDF
  const singleRef = useRef(null);

  // ---- bulk export ----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState('section'); // section | teacher
  const [bulkClass, setBulkClass] = useState('all');
  const [bulkTeacher, setBulkTeacher] = useState('all');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkSheets, setBulkSheets] = useState([]);
  const bulkRef = useRef(null);

  const periods = (config?.periods || []);

  const viewLabel = useMemo(() => {
    if (mode === 'teacher') return (teachers.find((tc) => tc.id === teacherId) || {}).name || '';
    const cn = (classes.find((c) => c.id === classId) || {}).name || '';
    const sn = (sections.find((s) => s.id === sectionId) || {}).name || '';
    return [cn, sn].filter(Boolean).join(' — ');
  }, [mode, teacherId, classId, sectionId, teachers, classes, sections]);

  useEffect(() => {
    if (!classId) { setSections([]); setSectionId(''); return; }
    schoolApi.listSections(orgId, classId, academicYearId).then((s) => { setSections(s || []); setSectionId((s && s[0]?.id) || ''); });
  }, [classId, orgId, academicYearId]);

  const reload = useCallback(async () => {
    setUnplaced([]);
    if (mode === 'section' && !sectionId) { setEntries([]); setLessons([]); return; }
    if (mode === 'student' && !sectionId) { setEntries([]); return; }
    if (mode === 'teacher' && !teacherId) { setEntries([]); return; }
    setLoading(true);
    try {
      if (mode === 'section') {
        const [ents, less] = await Promise.all([
          schoolApi.ttListEntries({ organization_id: orgId, section_id: sectionId, academic_year_id: academicYearId }),
          schoolApi.ttListLessons({ organization_id: orgId, section_id: sectionId, academic_year_id: academicYearId }),
        ]);
        setEntries(ents || []); setLessons(less || []);
      } else if (mode === 'student') {
        const pair = await schoolApi.ttGetPair({ organization_id: orgId, section_id: sectionId, academic_year_id: academicYearId });
        const entCalls = [schoolApi.ttListEntries({ organization_id: orgId, section_id: sectionId, academic_year_id: academicYearId })];
        const lessCalls = [schoolApi.ttListLessons({ organization_id: orgId, section_id: sectionId, academic_year_id: academicYearId })];
        if (pair?.paired_section_id) {
          entCalls.push(schoolApi.ttListEntries({ organization_id: orgId, section_id: pair.paired_section_id, academic_year_id: academicYearId }));
          lessCalls.push(schoolApi.ttListLessons({ organization_id: orgId, section_id: pair.paired_section_id, academic_year_id: academicYearId }));
        }
        const [ents, less] = await Promise.all([Promise.all(entCalls), Promise.all(lessCalls)]);
        setEntries([].concat(...ents.map((r) => r || [])));
        setLessons([].concat(...less.map((r) => r || [])));
      } else {
        // Teacher view: load this teacher's entries AND every lesson requirement they
        // are assigned to (across sections) so the Missing Lessons bar can appear here too.
        const [ents, less] = await Promise.all([
          schoolApi.ttListEntries({ organization_id: orgId, teacher_id: teacherId, academic_year_id: academicYearId }),
          schoolApi.ttListLessons({ organization_id: orgId, teacher_id: teacherId, academic_year_id: academicYearId }),
        ]);
        setEntries(ents || []); setLessons(less || []);
      }
    } finally { setLoading(false); }
  }, [mode, orgId, sectionId, teacherId, academicYearId]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { setSwapMode(false); setSwapFrom(null); setPlaceLessonId(null); setLegalSet(new Set()); setPairedBlockedSet(new Set()); }, [sectionId, mode, teacherId]);
  useEffect(() => { if (swapMode) { setPlaceLessonId(null); setLegalSet(new Set()); setPairedBlockedSet(new Set()); } }, [swapMode]);

  // load any saved pairing for the current section (drives the Generate dialog + Student view)
  useEffect(() => {
    if (!sectionId) { setSavedPair(null); return; }
    schoolApi.ttGetPair({ organization_id: orgId, section_id: sectionId, academic_year_id: academicYearId })
      .then((p) => setSavedPair(p?.paired_section_id ? p : null)).catch(() => setSavedPair(null));
  }, [sectionId, orgId, academicYearId]);

  // By-Teacher grid: load the selected teacher's blocked (unavailable) slots so they
  // render as hard-blocked cells (grey stripe + lock + reason) and can't be assigned.
  useEffect(() => {
    if (mode !== 'teacher' || !teacherId) { setTeacherBlocks({}); return; }
    let alive = true;
    schoolApi.ttGetAvailability({ organization_id: orgId, teacher_id: teacherId, academic_year_id: academicYearId })
      .then((av) => {
        if (!alive) return;
        const m = {};
        (av?.unavailable || []).forEach((u) => { m[`${u.day}:${u.period_seq}`] = u.reason || ''; });
        setTeacherBlocks(m);
      })
      .catch(() => { if (alive) setTeacherBlocks({}); });
    return () => { alive = false; };
  }, [mode, teacherId, orgId, academicYearId]);

  // sections list for the chosen paired class in the Generate dialog
  useEffect(() => {
    if (!pairClassId || pairClassId === 'none') { setPairSections([]); return; }
    schoolApi.listSections(orgId, pairClassId, academicYearId).then((s) => setPairSections(s || []));
  }, [pairClassId, orgId, academicYearId]);

  const loadWorkload = useCallback(async () => {
    try { setWorkload(await schoolApi.ttWorkload({ organization_id: orgId, academic_year_id: academicYearId })); }
    catch (e) { /* non-blocking */ }
  }, [orgId, academicYearId]);
  useEffect(() => { loadWorkload(); }, [loadWorkload]);

  const teacherLoad = useMemo(() => {
    // merge backend workload with the full teacher list so idle teachers still show
    const total = workload?.total_slots ?? 0;
    const byId = {};
    (teachers || []).forEach((tc) => {
      byId[tc.id] = { teacher_id: tc.id, teacher_name: tc.name, assigned: 0, placed: 0, unplaced: 0, blocked: 0, capacity: total, overloaded: false, status: 'idle', max_day: 0 };
    });
    (workload?.teachers || []).forEach((w) => { byId[w.teacher_id] = w; });
    const rank = { overloaded: 0, none: 1, partial: 2, full: 3, idle: 4 };
    return Object.values(byId).sort((a, b) =>
      (rank[a.status] ?? 9) - (rank[b.status] ?? 9)
      || (b.unplaced || 0) - (a.unplaced || 0)
      || (b.assigned || 0) - (a.assigned || 0)
      || String(a.teacher_name).localeCompare(String(b.teacher_name)));
  }, [workload, teachers]);

  const selectedLoad = useMemo(() => (teacherLoad.find((w) => w.teacher_id === teacherId) || null), [teacherLoad, teacherId]);

  const entryMap = useMemo(() => {
    const m = {};
    entries.forEach((e) => { m[cellKey(e.day, e.period_seq)] = e; });
    return m;
  }, [entries]);

  // How many periods of each subject are already placed, counted per SECTION+subject
  // so a teacher who teaches the same subject in two classes is measured correctly.
  const placedByLesson = useMemo(() => {
    const m = {};
    entries.forEach((e) => {
      if (!e.subject_id) return;
      const k = `${e.section_id}:${e.subject_id}`;
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [entries]);

  const lessonsWithMissing = useMemo(() => lessons.map((l) => {
    const required = Number(l.periods_per_week || 0);
    const placed = placedByLesson[`${l.section_id}:${l.subject_id}`] || 0;
    return { ...l, _required: required, _placed: placed, _missing: Math.max(0, required - placed) };
  }), [lessons, placedByLesson]);

  const missingLessons = useMemo(
    () => lessonsWithMissing.filter((l) => l._missing > 0),
    [lessonsWithMissing]);

  // Teacher view groups the missing pills by the section they belong to.
  const missingBySection = useMemo(() => {
    const groups = {};
    missingLessons.forEach((l) => {
      const key = l.section_id || '__none__';
      if (!groups[key]) {
        groups[key] = {
          section_id: key,
          label: [l.class_name, l.section_name].filter(Boolean).join(' — ') || l.section_name || l.class_name || '—',
          items: [],
        };
      }
      groups[key].items.push(l);
    });
    return Object.values(groups).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [missingLessons]);

  // ---- Manual Placement Helper ----
  const placeLesson = useMemo(
    () => (placeLessonId ? lessonsWithMissing.find((l) => l.id === placeLessonId) || null : null),
    [placeLessonId, lessonsWithMissing]);

  const fetchLegalSlots = useCallback(async (lesson) => {
    if (!lesson) { setLegalSet(new Set()); setPairedBlockedSet(new Set()); setPairedBlockLabel(''); return; }
    setLegalLoading(true);
    try {
      // Student view: the other program's section must also be free at that slot.
      let paired = null;
      if (mode === 'student') {
        paired = lesson.section_id === sectionId ? (savedPair?.paired_section_id || null) : sectionId;
      }
      const res = await schoolApi.ttLegalSlots({
        organization_id: orgId, academic_year_id: academicYearId,
        section_id: lesson.section_id, class_id: lesson.class_id,
        subject_id: lesson.subject_id, teacher_id: lesson.teacher_id || null,
        respect_program_windows: respectWindows, paired_section_id: paired,
      });
      setLegalSet(new Set((res?.slots || []).map((s) => `${s.day}:${s.period_seq}`)));
      setPairedBlockedSet(new Set((res?.paired_blocked || []).map((s) => `${s.day}:${s.period_seq}`)));
      setPairedBlockLabel(res?.paired_label || '');
    } catch (e) {
      setLegalSet(new Set()); setPairedBlockedSet(new Set()); setPairedBlockLabel('');
      toast.error(e?.response?.data?.detail || t('timetable.legalFailed', 'Could not compute legal slots'));
    } finally { setLegalLoading(false); }
  }, [mode, orgId, academicYearId, sectionId, savedPair, respectWindows, t]);

  const selectMissing = (lesson) => {
    if (swapMode) { setSwapMode(false); setSwapFrom(null); }
    if (placeLessonId === lesson.id) { setPlaceLessonId(null); setLegalSet(new Set()); setPairedBlockedSet(new Set()); return; }
    setPlaceLessonId(lesson.id);
    fetchLegalSlots(lesson);
  };

  // Keep highlights fresh after each placement; auto-clear when a lesson is full.
  useEffect(() => {
    if (!placeLessonId) { setLegalSet(new Set()); setPairedBlockedSet(new Set()); return; }
    const l = lessonsWithMissing.find((x) => x.id === placeLessonId);
    if (!l || l._missing <= 0) { setPlaceLessonId(null); setLegalSet(new Set()); setPairedBlockedSet(new Set()); return; }
    fetchLegalSlots(l);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, placeLessonId]);

  const assignMissing = async (lesson, day, seq) => {
    if (!lesson || placing) return;
    setPlacing(true);
    try {
      await schoolApi.ttUpsertEntry({
        organization_id: orgId, academic_year_id: academicYearId,
        class_id: lesson.class_id, section_id: lesson.section_id,
        day, period_seq: seq, subject_id: lesson.subject_id,
        teacher_id: lesson.teacher_id || null, lesson_id: lesson.id || null,
        uv_group: lesson.uv_group || undefined,
      });
      toast.success(`${t('timetable.placed', 'Placed')} ${lesson.label || lesson.subject_name}`);
      await reload(); loadWorkload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('timetable.couldNotPlace', 'Could not place lesson'));
    } finally { setPlacing(false); }
  };

  const openGenerate = () => {
    if (!sectionId) { toast.error(t('timetable.selectSectionFirst', 'Select a section first')); return; }
    // preset the dialog from any saved pairing
    if (savedPair?.paired_section_id) {
      setPairClassId(savedPair.paired_class_id || 'none');
      setPairSectionId(savedPair.paired_section_id);
    } else {
      setPairClassId('none'); setPairSectionId('');
    }
    // load cycle names for the selective-generate picker
    schoolApi.ttGetCycles({ organization_id: orgId, academic_year_id: academicYearId })
      .then((c) => setCyclesList((c?.cycles || []).filter((x) => (x.class_ids || []).length > 0)))
      .catch(() => setCyclesList([]));
    setGenOpen(true);
  };

  const STEP_LABELS = {
    queued: t('timetable.stepQueued', 'Queued…'),
    preparing: t('timetable.stepPreparing', 'Preparing…'),
    loading: t('timetable.stepLoading', 'Loading setup…'),
    solving: t('timetable.stepSolving', 'Solving timetable…'),
    saving: t('timetable.stepSaving', 'Saving results…'),
    done: t('timetable.stepDone', 'Done'),
  };

  const cancelGenerate = async () => {
    genCancelRef.current = true;
    const jid = genJobId;
    if (jid) { try { await schoolApi.ttGenerateCancel(jid); } catch (_e) { /* ignore */ } }
    setGenerating(false); setGenJobId(null); setGenProgress(0); setGenStep('');
    toast.message(t('timetable.genCancelled', 'Generation cancelled'));
  };

  const doGenerate = async () => {
    if (!sectionId) return;
    const paired = (pairClassId !== 'none' && pairSectionId) ? pairSectionId : null;
    genCancelRef.current = false;
    setGenerating(true); setGenProgress(3); setGenStep(STEP_LABELS.queued); setGenJobId(null);
    try {
      // persist / clear the pairing so it's remembered next time and in Student view
      await schoolApi.ttSetPair({ organization_id: orgId, academic_year_id: academicYearId, section_id: sectionId, paired_section_id: paired });
      const body = { organization_id: orgId, academic_year_id: academicYearId, seed: Math.floor(Math.random() * 1000), respect_program_windows: respectWindows, time_limit: solveTime };
      if (genCycle !== 'all') {
        // Selective per-cycle generation: only this cycle's classes are (re)built;
        // every other cycle is left completely untouched (incremental save).
        body.cycle = genCycle;
      } else if (genAll) {
        // Whole-school single pass: regenerate every section together so shared
        // teachers can be balanced across classes. Only LOCKED lessons stay fixed.
        body.regenerate_all = true;
      } else if (paired) {
        body.section_ids = [sectionId, paired]; body.groups = [[sectionId, paired]];
      } else {
        body.section_ids = [sectionId];
      }
      // Enqueue the background solve — the endpoint returns 202 + a job id.
      const { job_id: jobId } = await schoolApi.ttGenerate(body);
      if (!jobId) throw new Error('No job id returned');
      setGenJobId(jobId);

      // Poll the job status until it finishes (or the user cancels).
      let res = null;
      for (let i = 0; i < 200; i++) {
        if (genCancelRef.current) return;
        await new Promise((r) => setTimeout(r, 1200));
        let job;
        try { job = await schoolApi.ttGenerateStatus(jobId); } catch (_e) { continue; }
        if (genCancelRef.current) return;
        setGenProgress(job.progress || 0);
        setGenStep(STEP_LABELS[job.step] || job.step || '');
        if (job.status === 'completed') { res = job.result || {}; break; }
        if (job.status === 'failed') { throw new Error(job.error || 'Generation failed'); }
        if (job.status === 'cancelled' || job.status === 'superseded') { return; }
      }
      if (!res) throw new Error(t('timetable.genTimeout', 'Generation timed out — try a shorter search time.'));

      setUnplaced(res.unplaced || []);
      setConflictReport(res.conflict_report || []);
      setGenOpen(false);
      // refresh saved pairing state
      const p = await schoolApi.ttGetPair({ organization_id: orgId, section_id: sectionId, academic_year_id: academicYearId });
      setSavedPair(p?.paired_section_id ? p : null);
      toast.success(`${t('timetable.placed', 'Placed')} ${res.placed} · ${t('timetable.unplaced', 'Unplaced')} ${(res.unplaced || []).length}`);
      reload(); loadWorkload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Generation failed');
    } finally { setGenerating(false); setGenJobId(null); setGenProgress(0); setGenStep(''); }
  };

  // ---- Teacher availability editor (opened from a Fix-It action) ----
  const teachPeriods = ((config?.periods) || []).filter((p) => !p.is_break);
  const openAvailability = async (teacherId, teacherName) => {
    if (!teacherId) { toast.error(t('timetable.noTeacherOnLesson', 'This lesson has no teacher assigned')); return; }
    setAvailTeacher({ id: teacherId, name: teacherName });
    setAvailOpen(true); setAvailLoading(true);
    try {
      const av = await schoolApi.ttGetAvailability({ organization_id: orgId, teacher_id: teacherId });
      const set = new Set((av?.unavailable || []).map((u) => `${u.day}:${u.period_seq}`));
      setAvailBlocked(set);
      setAvailMaxPerDay(av?.max_per_day != null ? String(av.max_per_day) : '');
    } catch (e) { toast.error('Failed to load availability'); }
    finally { setAvailLoading(false); }
  };
  const toggleAvailCell = (day, seq) => {
    if (!canEdit) return;
    setAvailBlocked((prev) => {
      const n = new Set(prev); const k = `${day}:${seq}`;
      n.has(k) ? n.delete(k) : n.add(k); return n;
    });
  };
  const saveAvailability = async () => {
    if (!availTeacher) return;
    setAvailSaving(true);
    try {
      const unavailable = [...availBlocked].map((k) => { const [day, seq] = k.split(':'); return { day, period_seq: Number(seq) }; });
      await schoolApi.ttSaveAvailability({
        organization_id: orgId, teacher_id: availTeacher.id, unavailable,
        max_per_day: availMaxPerDay === '' ? null : Number(availMaxPerDay),
      });
      setAvailOpen(false);
      toast.success(t('timetable.availabilitySaved', 'Availability saved — regenerate to apply'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save availability'); }
    finally { setAvailSaving(false); }
  };

  // ---- Fix-It: route each conflict to the fastest resolution ----
  const TEACHER_CODES = new Set(['teacher_capacity', 'teacher_busy_window', 'availability_empty']);
  const WINDOW_CODES = new Set(['window_empty', 'window_saturated', 'section_full_window']);
  const fixIt = (c) => {
    if (TEACHER_CODES.has(c.code)) { openAvailability(c.teacher_id, c.teacher_name); return; }
    if (WINDOW_CODES.has(c.code)) {
      onGoToSetup && onGoToSetup();
      toast.info(t('timetable.fixWindowHint', 'Open Program Windows in Setup to widen the allowed blocks for this class/section.'));
      return;
    }
    if (c.code === 'no_consecutive_block') {
      onGoToSetup && onGoToSetup();
      toast.info(t('timetable.fixBlockHint', 'In Lesson Requirements, turn OFF “Double” for this subject so it can be split across the week.'));
      return;
    }
    // search_exhausted or unknown -> try a longer search
    setSolveTime(60); setGenOpen(true);
    toast.info(t('timetable.fixLongerHint', 'Search time set to Max — generate again to let the solver try harder.'));
  };
  const fixItLabel = (c) => {
    if (TEACHER_CODES.has(c.code)) return t('timetable.fixOpenAvailability', 'Open availability');
    if (WINDOW_CODES.has(c.code)) return t('timetable.fixReviewWindows', 'Review windows');
    if (c.code === 'no_consecutive_block') return t('timetable.fixAllowSplit', 'Allow split');
    return t('timetable.fixLonger', 'Try longer search');
  };

  // ---- Conflict Report export (CSV + printable PDF) ----
  const exportConflictsCsv = () => {
    const header = ['Subject', 'Section', 'Teacher', 'Periods', 'Cause', 'Reason'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = conflictReport.map((c) => [c.subject_name, c.section_name, c.teacher_name, c.size, c.code, c.reason].map(esc).join(','));
    const csv = [header.map(esc).join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `conflict-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const exportConflictsPdf = () => {
    const esc = (v) => String(v ?? '').replace(/[&<>]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]));
    const rows = conflictReport.map((c) => `<tr><td>${esc(c.subject_name)}</td><td>${esc(c.section_name)}</td><td>${esc(c.teacher_name)}</td><td style="text-align:center">${esc(c.size)}</td><td>${esc(c.reason)}</td></tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Conflict Report</title>
      <style>body{font-family:Figtree,Arial,sans-serif;color:#1f2430;padding:28px}h1{font-size:20px;margin:0 0 4px}p{color:#667085;margin:0 0 18px;font-size:12px}
      table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #e4e7ec;padding:8px 10px;text-align:left;vertical-align:top}
      th{background:#f9fafb;font-weight:600}</style></head><body>
      <h1>Timetable Conflict Report</h1><p>Generated ${new Date().toLocaleString()} — ${conflictReport.length} unresolved lesson(s)</p>
      <table><thead><tr><th>Subject</th><th>Section</th><th>Teacher</th><th>Periods</th><th>Reason</th></tr></thead><tbody>${rows}</tbody></table>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error(t('timetable.popupBlocked', 'Allow pop-ups to export the PDF')); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };


  const clearSection = async () => {
    if (!sectionId) return;
    try { await schoolApi.ttClearEntries({ organization_id: orgId, section_id: sectionId }); reload(); loadWorkload(); toast.success(t('timetable.cleared', 'Cleared')); }
    catch (e) { toast.error('Failed to clear'); }
  };

  const doClearAll = async () => {
    setClearingAll(true);
    try {
      const body = { organization_id: orgId, academic_year_id: academicYearId };
      if (clearScope === 'class' && classId && sections.length) {
        body.section_ids = sections.map((s) => s.id);
      }
      const res = await schoolApi.ttClearAll(body);
      setClearAllOpen(false);
      reload(); loadWorkload();
      toast.success(`${t('timetable.clearedAll', 'Timetable cleared')} (${res?.deleted ?? 0})`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to clear all'); }
    finally { setClearingAll(false); }
  };

  const clearTeacher = async () => {
    if (!teacherId) return;
    setClearingTeacher(true);
    try {
      const res = await schoolApi.ttClearEntries({ organization_id: orgId, teacher_id: teacherId });
      setClearTeacherOpen(false);
      reload(); loadWorkload();
      toast.success(`${t('timetable.cleared', 'Cleared')} ${res?.count ?? ''}`.trim());
    } catch (e) { toast.error('Failed to clear'); }
    finally { setClearingTeacher(false); }
  };

  const doSwap = async (target) => {
    if (!swapFrom) return;
    if (swapFrom.day === target.day && swapFrom.seq === target.seq) { setSwapFrom(null); return; }
    setSwapping(true);
    try {
      if (mode === 'teacher') {
        if (!teacherId) return;
        await schoolApi.ttTeacherMove({
          organization_id: orgId, teacher_id: teacherId,
          from: { day: swapFrom.day, period_seq: swapFrom.seq },
          to: { day: target.day, period_seq: target.seq },
        });
      } else if (mode === 'student') {
        if (!sectionId) return;
        await schoolApi.ttStudentSwap({
          organization_id: orgId, academic_year_id: academicYearId, section_id: sectionId,
          paired_section_id: savedPair?.paired_section_id || null,
          a: { day: swapFrom.day, period_seq: swapFrom.seq },
          b: { day: target.day, period_seq: target.seq },
        });
      } else {
        if (!sectionId) return;
        await schoolApi.ttSwapEntries({
          organization_id: orgId, academic_year_id: academicYearId, section_id: sectionId,
          a: { day: swapFrom.day, period_seq: swapFrom.seq },
          b: { day: target.day, period_seq: target.seq },
        });
      }
      setSwapFrom(null);
      reload(); loadWorkload();
      toast.success(t('timetable.swapped', 'Lessons rearranged'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not rearrange');
    } finally { setSwapping(false); }
  };

  const saveCell = async (subjectId) => {
    if (!editCell) return;
    const lesson = lessons.find((l) => l.subject_id === subjectId);
    try {
      await schoolApi.ttUpsertEntry({
        organization_id: orgId, academic_year_id: academicYearId, class_id: classId, section_id: sectionId,
        day: editCell.day, period_seq: editCell.seq, subject_id: subjectId,
        teacher_id: lesson?.teacher_id || null, lesson_id: lesson?.id || null,
        uv_group: lesson?.uv_group || undefined,
      });
      setEditCell(null); reload(); loadWorkload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not place lesson'); }
  };

  const clearCell = async () => {
    if (!editCell?.entry) { setEditCell(null); return; }
    try { await schoolApi.ttDeleteEntry(editCell.entry.id); setEditCell(null); reload(); loadWorkload(); }
    catch (e) { toast.error('Failed'); }
  };

  const toggleLockCell = async () => {
    if (!editCell?.entry) return;
    const next = !editCell.entry.fixed;
    try {
      await schoolApi.ttLockEntry({ organization_id: orgId, entry_id: editCell.entry.id, fixed: next });
      setEditCell(null); reload();
      toast.success(next ? t('timetable.lockedSlot', 'Slot locked — Auto-Generate will keep it') : t('timetable.unlockedSlot', 'Slot unlocked'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const safeName = (s) => (s || 'timetable').toString().replace(/[^\w\-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const subtitleFor = (m, extra) => (m === 'teacher'
    ? `${t('timetable.teacherSchedule', 'Teacher schedule')}${extra ? ` · ${extra}` : ''}`
    : m === 'student'
      ? t('timetable.studentSchedule', 'Student schedule')
      : t('timetable.classSchedule', 'Class schedule'));

  const exportPdf = async () => {
    if (!hasData) return;
    setExporting(true);
    try {
      // For a paired / dual-program class, merge BOTH programs into ONE combined sheet
      // (exactly like Bulk Export) so the partner section's days/lessons — e.g. the
      // Friday column that lives in the Português section — always appear.
      let sheetEntries = entries;
      let sheetMode = mode;
      let title = viewLabel;
      if (mode === 'section' && savedPair?.paired_section_id) {
        try {
          const pairedEnts = await schoolApi.ttListEntries({ organization_id: orgId, section_id: savedPair.paired_section_id, academic_year_id: academicYearId });
          sheetEntries = (entries || []).concat(pairedEnts || []);
        } catch { /* fall back to this section's entries only */ }
        sheetMode = 'student';
        const pairedName = [savedPair.paired_class_name, savedPair.paired_section_name].filter(Boolean).join(' — ');
        if (pairedName) title = `${viewLabel}  +  ${pairedName}`;
      }
      // Render a branded printable sheet (ELI header on the page), then capture it.
      setSingleSheet({ key: 'single', title, subtitle: subtitleFor(sheetMode), mode: sheetMode, entries: sheetEntries });
      await new Promise((r) => setTimeout(r, 300));
      const el = singleRef.current;
      if (!el) throw new Error('render container missing');
      await awaitImages(el); // ensure the logo is fully loaded before snapshot
      const filename = `Timetable_${safeName(title)}${yearName ? `_${safeName(yearName)}` : ''}.pdf`;
      const pdf = await nodeToOnePagePdf(el);
      pdf.save(filename);
      toast.success(t('timetable.pdfDone', 'PDF exported'));
    } catch (e) {
      toast.error(t('timetable.pdfFailed', 'Failed to export PDF'));
    } finally { setExporting(false); setSingleSheet(null); }
  };

  const hasData = (mode === 'section' && sectionId) || (mode === 'student' && sectionId) || (mode === 'teacher' && teacherId);

  const runBulkExport = async () => {
    setBulkBusy(true);
    try {
      const all = await schoolApi.ttListEntries({ organization_id: orgId, academic_year_id: academicYearId });
      let sheets = [];
      if (bulkMode === 'section') {
        const targetClasses = bulkClass === 'all' ? classes : classes.filter((c) => c.id === bulkClass);
        const processed = new Set(); // avoid emitting a paired section twice
        for (const c of targetClasses) {
          const secs = await schoolApi.listSections(orgId, c.id, academicYearId);
          for (const s of (secs || [])) {
            if (processed.has(s.id)) continue;
            let ents = (all || []).filter((e) => e.section_id === s.id);
            let sheetMode = 'section';
            let title = `${c.name} — ${s.name}`;
            // If this section has a configured paired class, merge BOTH schedules into
            // one combined weekly timetable (exactly like the on-screen Student view).
            let pair = null;
            try {
              pair = await schoolApi.ttGetPair({ organization_id: orgId, section_id: s.id, academic_year_id: academicYearId });
            } catch { pair = null; }
            if (pair?.paired_section_id) {
              const pairedEnts = (all || []).filter((e) => e.section_id === pair.paired_section_id);
              ents = ents.concat(pairedEnts);
              sheetMode = 'student';
              const pairedName = [pair.paired_class_name, pair.paired_section_name].filter(Boolean).join(' — ');
              if (pairedName) title = `${title}  +  ${pairedName}`;
              processed.add(pair.paired_section_id); // don't emit the partner as its own sheet
            }
            if (!ents.length) continue;
            processed.add(s.id);
            sheets.push({ key: s.id, title, subtitle: subtitleFor(sheetMode), mode: sheetMode, entries: ents });
          }
        }
      } else {
        const byT = {};
        (all || []).forEach((e) => {
          if (!e.teacher_id) return;
          if (bulkTeacher !== 'all' && e.teacher_id !== bulkTeacher) return;
          if (!byT[e.teacher_id]) byT[e.teacher_id] = { name: e.teacher_name || e.teacher_id, entries: [] };
          byT[e.teacher_id].entries.push(e);
        });
        sheets = Object.entries(byT)
          .sort((a, b) => (a[1].name || '').localeCompare(b[1].name || ''))
          .map(([tid, v]) => ({ key: tid, title: v.name, subtitle: subtitleFor('teacher', `${v.entries.length} ${t('timetable.periodsLower', 'periods')}`), mode: 'teacher', entries: v.entries }));
      }
      if (!sheets.length) {
        toast.error(t('timetable.bulkNoData', 'No scheduled timetables found to export'));
        return;
      }
      setBulkSheets(sheets);
      // allow the hidden container to render before capturing
      await new Promise((r) => setTimeout(r, 300));
      const container = bulkRef.current;
      if (!container) throw new Error('render container missing');
      await awaitImages(container); // ensure every logo is loaded before snapshot
      const nodes = container.querySelectorAll('[data-tt-sheet]');
      // Each teacher/class becomes its OWN branded, single-page PDF so it can be
      // identified at a glance. A single target downloads directly; multiple targets
      // are zipped. nodeToOnePagePdf guarantees each sheet fits ONE A4 landscape page.
      if (sheets.length === 1) {
        const fn = `Timetable_${safeName(sheets[0].title)}${yearName ? `_${safeName(yearName)}` : ''}.pdf`;
        const pdf = await nodeToOnePagePdf(nodes[0]);
        pdf.save(fn);
      } else {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        for (let i = 0; i < sheets.length; i++) {
          const fn = `Timetable_${safeName(sheets[i].title)}${yearName ? `_${safeName(yearName)}` : ''}.pdf`;
          // eslint-disable-next-line no-await-in-loop
          const pdf = await nodeToOnePagePdf(nodes[i]);
          zip.file(fn, pdf.output('blob'));
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        const scope = bulkMode === 'teacher'
          ? (bulkTeacher === 'all' ? 'AllTeachers' : safeName((teachers.find((tc) => tc.id === bulkTeacher) || {}).name))
          : (bulkClass === 'all' ? 'AllSections' : safeName((classes.find((c) => c.id === bulkClass) || {}).name));
        a.href = url; a.download = `Timetables_${scope}${yearName ? `_${safeName(yearName)}` : ''}.zip`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
      toast.success(`${t('timetable.pdfDone', 'PDF exported')} · ${sheets.length} ${t('timetable.pages', 'page(s)')}`);
      setBulkOpen(false);
    } catch (e) {
      toast.error(t('timetable.pdfFailed', 'Failed to export PDF'));
    } finally {
      setBulkBusy(false);
      setBulkSheets([]);
    }
  };


  return (
    <Card className="p-5" data-testid="tt-grid-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="h-9">
            <TabsTrigger value="section" data-testid="tt-mode-section"><Users className="w-3.5 h-3.5 mr-1" /> {t('timetable.byClass', 'By Class')}</TabsTrigger>
            <TabsTrigger value="teacher" data-testid="tt-mode-teacher"><GraduationCap className="w-3.5 h-3.5 mr-1" /> {t('timetable.byTeacher', 'By Teacher')}</TabsTrigger>
            <TabsTrigger value="student" data-testid="tt-mode-student"><Users className="w-3.5 h-3.5 mr-1" /> {t('timetable.byStudent', 'Student')}</TabsTrigger>
          </TabsList>
        </Tabs>
        {(mode === 'section' || mode === 'student') ? (
          <>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger className="w-48" data-testid="tt-grid-class"><SelectValue placeholder={t('timetable.selectClass', 'Select class')} /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sectionId} onValueChange={setSectionId} disabled={!sections.length}>
              <SelectTrigger className="w-40" data-testid="tt-grid-section"><SelectValue placeholder={t('timetable.selectSection', 'Section')} /></SelectTrigger>
              <SelectContent>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            {mode === 'section' && canEdit && sectionId && <>
              <Button onClick={openGenerate} disabled={generating} size="sm" className="gap-2" data-testid="tt-generate">{generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {t('timetable.autoGenerate', 'Auto-Generate')}</Button>
              <Button onClick={() => { setSwapMode((v) => !v); setSwapFrom(null); }} variant={swapMode ? 'default' : 'outline'} size="sm" className="gap-2" data-testid="tt-swap-toggle" aria-pressed={swapMode}>
                <ArrowLeftRight className="w-4 h-4" /> {swapMode ? t('timetable.swapping', 'Swapping…') : t('timetable.swap', 'Swap')}
              </Button>
              <Button onClick={clearSection} variant="outline" size="sm" className="gap-2 text-destructive" data-testid="tt-clear"><Eraser className="w-4 h-4" /> {t('timetable.clear', 'Clear')}</Button>
            </>}
            {canEdit && (
              <Button onClick={() => { setClearScope(classId && sections.length ? 'class' : 'year'); setClearAllOpen(true); }} variant="outline" size="sm" className="gap-2 text-destructive border-destructive/40" data-testid="tt-clear-all"><Trash2 className="w-4 h-4" /> {t('timetable.clearAll', 'Clear All')}</Button>
            )}
            {mode === 'student' && sectionId && (
              <>
                <Badge variant={savedPair ? 'secondary' : 'outline'} className="self-center" data-testid="tt-student-pair-badge">
                  {savedPair ? `${t('timetable.paired', 'Paired')}: ${savedPair.paired_class_name || ''} ${savedPair.paired_section_name || ''}`.trim() : t('timetable.noPair', 'No paired program')}
                </Badge>
                {canEdit && (
                  <Button onClick={() => { setSwapMode((v) => !v); setSwapFrom(null); }} variant={swapMode ? 'default' : 'outline'} size="sm" className="gap-2" data-testid="tt-swap-toggle-student" aria-pressed={swapMode}>
                    <ArrowLeftRight className="w-4 h-4" /> {swapMode ? t('timetable.swapping', 'Swapping…') : t('timetable.swap', 'Swap')}
                  </Button>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <TeacherPicker teachers={teachers} value={teacherId} onChange={setTeacherId}
              placeholder={t('timetable.selectTeacher', 'Select teacher')} testId="tt-grid-teacher" />
            {teacherId && selectedLoad && (
              <div className="flex items-center gap-1.5" data-testid="tt-teacher-load-badges">
                <Badge variant="secondary" data-testid="tt-teacher-assigned">{t('timetable.assigned', 'Assigned')}: {selectedLoad.assigned}</Badge>
                <Badge variant="outline" data-testid="tt-teacher-periods">{t('timetable.placed', 'Placed')}: {selectedLoad.placed}</Badge>
                {selectedLoad.unplaced > 0 && (
                  <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30" data-testid="tt-teacher-unplaced">{t('timetable.unplaced', 'Unplaced')}: {selectedLoad.unplaced}</Badge>
                )}
                {selectedLoad.overloaded && (
                  <Badge className="bg-destructive/15 text-destructive border border-destructive/30 gap-1" data-testid="tt-teacher-overloaded"><AlertTriangle className="w-3 h-3" /> {t('timetable.overloaded', 'Overloaded')}</Badge>
                )}
              </div>
            )}
            {canEdit && teacherId && (
              <>
                <Button onClick={() => { setSwapMode((v) => !v); setSwapFrom(null); }} variant={swapMode ? 'default' : 'outline'} size="sm" className="gap-2" data-testid="tt-swap-toggle-teacher" aria-pressed={swapMode}>
                  <ArrowLeftRight className="w-4 h-4" /> {swapMode ? t('timetable.swapping', 'Swapping…') : t('timetable.swap', 'Swap')}
                </Button>
                <Button onClick={() => setClearTeacherOpen(true)} variant="outline" size="sm" className="gap-2 text-destructive" data-testid="tt-clear-teacher">
                  <Eraser className="w-4 h-4" /> {t('timetable.clearTeacher', 'Clear teacher')}
                </Button>
              </>
            )}
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={() => { loadWorkload(); setWorkloadOpen(true); }} variant="outline" size="sm" className="gap-2" data-testid="tt-workload-open">
            <BarChart3 className="w-4 h-4" /> {t('timetable.workload', 'Workload')}
          </Button>
          {hasData && (
            <Button onClick={exportPdf} disabled={exporting} variant="outline" size="sm" className="gap-2" data-testid="tt-export-pdf">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} {t('timetable.exportPdf', 'Export PDF')}
            </Button>
          )}
          <Button onClick={() => setBulkOpen(true)} variant="outline" size="sm" className="gap-2" data-testid="tt-bulk-export-open">
            <FileStack className="w-4 h-4" /> {t('timetable.bulkExport', 'Bulk Export')}
          </Button>
        </div>
      </div>

      {swapMode && (
        <div className="mb-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-primary flex items-center gap-2" data-testid="tt-swap-hint">
          <ArrowLeftRight className="w-4 h-4" />
          {swapFrom ? t('timetable.swapPick2', 'Now click the slot to swap with (or the same slot to cancel).') : t('timetable.swapPick1', 'Click a lesson to move, then click the slot to swap it with.')}
        </div>
      )}

      {unplaced.length > 0 && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" data-testid="tt-unplaced">
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          {unplaced.length} {t('timetable.unplacedMsg', 'lesson period(s) could not be placed:')}{' '}
          {unplaced.map((u, i) => <span key={i}>{u.label || u.subject_name}{i < unplaced.length - 1 ? ', ' : ''}</span>)}
        </div>
      )}

      {conflictReport.length > 0 && (
        <div className="mb-4 rounded-lg border border-border bg-card overflow-hidden" data-testid="tt-conflict-report">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/40">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="font-semibold text-sm">{t('timetable.conflictReport', 'Conflict Report')}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">{t('timetable.conflictReportHint', 'why these lessons could not be placed')}</span>
            <div className="ml-auto flex items-center gap-1.5">
              <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={exportConflictsCsv} data-testid="tt-conflict-export-csv"><Download className="w-3.5 h-3.5" /> CSV</Button>
              <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={exportConflictsPdf} data-testid="tt-conflict-export-pdf"><FileText className="w-3.5 h-3.5" /> PDF</Button>
            </div>
          </div>
          <div className="divide-y divide-border">
            {conflictReport.map((c, i) => (
              <div key={c.lesson_id || i} className="px-4 py-3 text-sm flex items-start justify-between gap-3" data-testid="tt-conflict-item">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-0.5">
                    <span className="font-medium">{c.subject_name}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{c.section_name}</span>
                    {c.teacher_name && c.teacher_name !== 'the teacher' && (
                      <Badge variant="outline" className="text-xs">{c.teacher_name}</Badge>
                    )}
                    {c.size > 1 && <Badge variant="secondary" className="text-xs">{c.size}× {t('timetable.block', 'block')}</Badge>}
                  </div>
                  <p className="text-muted-foreground leading-snug">{c.reason}</p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="secondary" className="h-8 gap-1.5 shrink-0" onClick={() => fixIt(c)} data-testid="tt-conflict-fixit">
                    <Wrench className="w-3.5 h-3.5" /> {fixItLabel(c)}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Placement Helper — Missing Lessons Panel */}
      {canEdit && !swapMode && (mode === 'section' || mode === 'student' || mode === 'teacher') && hasData && missingLessons.length > 0 && (
        <div className="mb-3 rounded-lg border border-border bg-card p-3" data-testid="tt-missing-panel">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-primary" />
            <span className="font-semibold text-sm">{t('timetable.missingLessons', 'Missing Lessons')}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {placeLesson
                ? t('timetable.missingActiveHint', 'Click a highlighted green cell to place it — click the chip again to cancel.')
                : t('timetable.missingHint', 'Click a subject to highlight where it can legally go, then click a cell to place it.')}
            </span>
            {legalLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            {placeLesson && (
              <span className="ml-auto text-xs font-medium text-emerald-600 dark:text-emerald-400" data-testid="tt-legal-count">
                {legalSet.size} {t('timetable.legalSlots', 'legal slot(s)')}
              </span>
            )}
          </div>
          {mode === 'teacher' ? (
            <div className="space-y-2">
              {missingBySection.map((grp) => (
                <div key={grp.section_id} data-testid={`tt-missing-group-${grp.section_id}`}>
                  <p className="text-[11px] font-medium text-muted-foreground mb-1">{grp.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {grp.items.map((l) => (
                      <MissingChip key={l.id} l={l} active={placeLessonId === l.id} onClick={() => selectMissing(l)} showSection={false} t={t} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {missingLessons.map((l) => (
                <MissingChip key={l.id} l={l} active={placeLessonId === l.id} onClick={() => selectMissing(l)} showSection={mode === 'student'} t={t} />
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? <InlineSpinner /> : (mode === 'section' && !sectionId) || (mode === 'student' && !sectionId) || (mode === 'teacher' && !teacherId) ? (
        <EmptyState icon={CalendarRange} title={t('timetable.selectToView', 'Select to view')} description={t('timetable.selectToViewDesc', 'Pick a class/section or a teacher to see the timetable grid.')} />
      ) : (
        <div className="overflow-x-auto" ref={gridRef}>
          <div className="px-1 pb-3">
            <h3 className="text-base font-semibold">{t('nav.timetable', 'Timetable')}{viewLabel ? ` — ${viewLabel}` : ''}</h3>
            <p className="text-xs text-muted-foreground">{mode === 'teacher' ? t('timetable.teacherSchedule', 'Teacher schedule') : mode === 'student' ? `${t('timetable.studentSchedule', 'Student schedule (all programs)')}${savedPair ? ` · ${savedPair.paired_class_name || ''} ${savedPair.paired_section_name || ''}`.trim() : ''}` : t('timetable.classSchedule', 'Class schedule')}</p>
          </div>
          <table className="w-full border-collapse text-sm" data-testid="tt-grid-table">
            <thead>
              <tr>
                <th className="border border-border bg-muted/50 p-2 text-xs w-28">{t('timetable.period', 'Period')}</th>
                {activeDays.map((d) => <th key={d.key} className="border border-border bg-muted/50 p-2 text-xs">{d.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p.seq} className={p.is_break ? 'bg-muted/30' : ''}>
                  <td className="border border-border p-2 text-xs text-center">
                    <div className="font-medium">{p.label}</div>
                    {p.start && <div className="text-[10px] text-muted-foreground">{p.start}–{p.end}</div>}
                  </td>
                  {activeDays.map((d) => {
                    if (p.is_break) return <td key={d.key} className="border border-border p-1 text-center text-[10px] text-muted-foreground">{t('timetable.break', 'Break')}</td>;
                    if (!periodRunsOn(p, d.key)) return <td key={d.key} className="border border-border p-1 text-center text-[10px] text-muted-foreground/40 bg-muted/40" data-testid={`tt-cell-${d.key}-${p.seq}-off`}>—</td>;
                    const e = entryMap[cellKey(d.key, p.seq)];
                    const isLegal = !!placeLesson && !e && legalSet.has(`${d.key}:${p.seq}`);
                    const isPairedBlocked = !!placeLesson && !e && !isLegal && pairedBlockedSet.has(`${d.key}:${p.seq}`);
                    // Teacher unavailability (blocked slot) — By-Teacher view, empty cell only.
                    const blockReason = (mode === 'teacher' && !e) ? teacherBlocks[`${d.key}:${p.seq}`] : undefined;
                    const isTeacherBlocked = blockReason !== undefined;
                    const clickable = canEdit && !isTeacherBlocked && (isLegal || (!placeLesson && (mode !== 'student' || swapMode || !!e)));
                    const isSwapSel = swapMode && swapFrom && swapFrom.day === d.key && swapFrom.seq === p.seq;
                    const onCellClick = !clickable ? undefined : () => {
                      // Manual Placement Helper: one-click assign into a highlighted legal cell
                      if (placeLesson) {
                        if (isLegal) assignMissing(placeLesson, d.key, p.seq);
                        return;
                      }
                      if (swapMode) {
                        if (swapping) return;
                        if (!swapFrom) {
                          if (!e) { toast.message(t('timetable.pickToMove', 'Pick a lesson to move first')); return; }
                          setSwapFrom({ day: d.key, seq: p.seq });
                        } else {
                          doSwap({ day: d.key, seq: p.seq });
                        }
                        return;
                      }
                      if (mode === 'section') { setEditCell({ day: d.key, seq: p.seq, entry: e }); }
                      else if (e) { setEditCell({ day: d.key, seq: p.seq, entry: e }); }
                      else if (mode === 'teacher') { setTeacherAdd({ day: d.key, seq: p.seq, dayLabel: d.label, periodLabel: p.label }); }
                    };
                    return (
                      <td key={d.key}
                        style={isPairedBlocked
                          ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(245,158,11,0.14) 0, rgba(245,158,11,0.14) 6px, rgba(245,158,11,0.04) 6px, rgba(245,158,11,0.04) 12px)' }
                          : isTeacherBlocked
                            ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(100,116,139,0.30) 0, rgba(100,116,139,0.30) 6px, rgba(100,116,139,0.10) 6px, rgba(100,116,139,0.10) 12px)' }
                            : undefined}
                        className={`border p-1 text-center align-middle h-14 min-w-[120px] relative ${isLegal ? 'ring-2 ring-emerald-500 ring-inset bg-emerald-500/10 border-emerald-500' : isPairedBlocked ? 'border-amber-500/60 cursor-not-allowed' : isTeacherBlocked ? 'border-slate-400/60 cursor-not-allowed' : isSwapSel ? 'ring-2 ring-primary ring-inset bg-primary/10 border-primary' : e?.fixed ? 'border-amber-400 bg-amber-50/60' : 'border-border'} ${clickable ? 'cursor-pointer hover:bg-accent/50' : ''} ${placeLesson && !isLegal && !isPairedBlocked && !e ? 'opacity-50' : ''}`}
                        onClick={onCellClick}
                        title={isTeacherBlocked
                          ? `${t('timetable.unavailable', 'Unavailable')}${blockReason ? `: ${blockReason}` : ''}`
                          : (isPairedBlocked && pairedBlockLabel ? `${t('timetable.pairedBusy', 'Paired class busy')}: ${pairedBlockLabel}` : undefined)}
                        data-testid={`tt-cell-${d.key}-${p.seq}`}>
                        {e?.fixed && <Lock className="w-3 h-3 text-amber-500 absolute top-0.5 right-0.5" data-testid={`tt-cell-locked-${d.key}-${p.seq}`} aria-label="locked" />}
                        {isTeacherBlocked && <Lock className="w-3 h-3 text-slate-500 absolute top-0.5 right-0.5" data-testid={`tt-cell-blocked-${d.key}-${p.seq}`} aria-label="unavailable" />}
                        {e ? (
                          <div>
                            <div className="font-medium text-xs leading-tight">{mode === 'teacher' ? [e.class_name, e.section_name].filter(Boolean).join(' — ') : (e.label || e.subject_name)}</div>
                            <div className="text-[10px] text-muted-foreground">{mode === 'teacher' ? (e.label || e.subject_name) : mode === 'student' ? [e.class_name, e.teacher_name].filter(Boolean).join(' · ') : e.teacher_name}</div>
                          </div>
                        ) : isLegal ? (
                          <span className="text-emerald-600 dark:text-emerald-400 text-xs font-medium inline-flex items-center gap-1" data-testid={`tt-legal-cell-${d.key}-${p.seq}`}><Plus className="w-3.5 h-3.5" /></span>
                        ) : isPairedBlocked ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-500" data-testid={`tt-paired-blocked-${d.key}-${p.seq}`}><Ban className="w-3 h-3" /> {t('timetable.pairedBlocked', 'Paired busy')}</span>
                        ) : isTeacherBlocked ? (
                          <span className="inline-flex flex-col items-center gap-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300" data-testid={`tt-teacher-blocked-${d.key}-${p.seq}`}>
                            <span className="inline-flex items-center gap-1">{t('timetable.unavailable', 'Unavailable')}</span>
                            {blockReason ? <span className="truncate max-w-[110px] text-[9px] text-muted-foreground font-normal">{blockReason}</span> : null}
                          </span>
                        ) : mode === 'teacher' ? (
                          <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/5 px-2 py-0.5 text-[10px] font-medium text-emerald-700/80 dark:text-emerald-400/80" data-testid={`tt-free-${d.key}-${p.seq}`}>{t('timetable.free', 'Free')}</span>
                        ) : <span className="text-muted-foreground/40 text-xs">+</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* assign dialog (section mode) / remove dialog (teacher mode filled cell) */}
      <Dialog open={!!editCell} onOpenChange={(o) => { if (!o) setEditCell(null); }}>
        <DialogContent data-testid="tt-cell-dialog">
          <DialogHeader><DialogTitle>{mode === 'section' ? t('timetable.assignLesson', 'Assign lesson') : t('timetable.lessonInSlot', 'Lesson in this slot')}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {mode === 'teacher' ? (
              editCell?.entry ? (
                <div className="rounded-md border border-border px-3 py-2">
                  <div className="font-medium text-sm">{[editCell.entry.class_name, editCell.entry.section_name].filter(Boolean).join(' — ')}</div>
                  <div className="text-xs text-muted-foreground">{editCell.entry.label || editCell.entry.subject_name}</div>
                </div>
              ) : null
            ) : mode === 'student' ? (
              editCell?.entry ? (
                <div className="rounded-md border border-border px-3 py-2 space-y-0.5" data-testid="tt-student-lesson-info">
                  <div className="font-medium text-sm">{editCell.entry.label || editCell.entry.subject_name}</div>
                  <div className="text-xs text-muted-foreground">{[editCell.entry.class_name, editCell.entry.section_name].filter(Boolean).join(' — ')}</div>
                  <div className="text-xs text-muted-foreground">{editCell.entry.teacher_name || '—'}</div>
                  <p className="text-[11px] text-muted-foreground pt-1">{t('timetable.studentEditHint', 'Use Swap to move this lesson to another slot. To change its subject or teacher, edit it in its own program under By Class.')}</p>
                </div>
              ) : null
            ) : (
              <>
                {(() => {
                  // Empty slot -> only the lessons still short of hours; filled slot ->
                  // the full list (so you can reassign) — both annotated with hours left.
                  const assignList = editCell?.entry ? lessonsWithMissing : missingLessons;
                  if (lessons.length === 0) {
                    return <p className="text-sm text-muted-foreground">{t('timetable.noLessonsDefine', 'Define lesson requirements in Setup first.')}</p>;
                  }
                  if (assignList.length === 0) {
                    return <p className="text-sm text-muted-foreground" data-testid="tt-all-scheduled">{t('timetable.allScheduled', 'All lessons are fully scheduled.')}</p>;
                  }
                  return assignList.map((l) => (
                    <button key={l.id} type="button" onClick={() => saveCell(l.subject_id)}
                      className="w-full text-left px-3 py-2 rounded-md border border-border hover:bg-accent flex items-center justify-between gap-2"
                      data-testid={`tt-assign-${l.subject_id}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-sm truncate">{l.label || l.subject_name}</span>
                        {l._missing > 0 && (
                          <span className="shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary" data-testid={`tt-missing-${l.subject_id}`}>
                            {l._missing} {t('timetable.hoursLeft', 'left')}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">{l.teacher_name || '—'}</span>
                    </button>
                  ));
                })()}
              </>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {editCell?.entry && (mode === 'section' || mode === 'student') && (
              <Button variant="outline" className={`gap-2 ${editCell.entry.fixed ? 'text-amber-600 border-amber-300' : ''}`} onClick={toggleLockCell} data-testid="tt-cell-lock">
                {editCell.entry.fixed ? <><LockOpen className="w-4 h-4" /> {t('timetable.unlockSlot', 'Unlock slot')}</> : <><Lock className="w-4 h-4" /> {t('timetable.lockSlot', 'Lock slot')}</>}
              </Button>
            )}
            {editCell?.entry && <Button variant="outline" className="text-destructive gap-2" onClick={clearCell} data-testid="tt-cell-clear"><Trash2 className="w-4 h-4" /> {(mode === 'teacher' || mode === 'student') ? t('timetable.removeLesson', 'Remove lesson') : t('timetable.clearCell', 'Clear cell')}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* add-for-teacher dialog (teacher mode empty cell) */}
      {teacherAdd && (
        <TeacherAddDialog orgId={orgId} academicYearId={academicYearId} teacherId={teacherId}
          teacherName={viewLabel} cell={teacherAdd} onClose={() => setTeacherAdd(null)}
          onSaved={() => { setTeacherAdd(null); reload(); loadWorkload(); }} t={t} />
      )}

      {/* clear-teacher confirm dialog */}
      <Dialog open={clearTeacherOpen} onOpenChange={(o) => { if (!clearingTeacher) setClearTeacherOpen(o); }}>
        <DialogContent data-testid="tt-clear-teacher-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eraser className="w-4 h-4" /> {t('timetable.clearTeacher', 'Clear teacher')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-1">{t('timetable.clearTeacherConfirm', 'This removes every lesson placed for this teacher across all classes and sections, leaving those slots empty. This cannot be undone.')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearTeacherOpen(false)} disabled={clearingTeacher} data-testid="tt-clear-teacher-cancel">{t('common.cancel', 'Cancel')}</Button>
            <Button className="text-destructive gap-2" variant="outline" onClick={clearTeacher} disabled={clearingTeacher} data-testid="tt-clear-teacher-confirm">
              {clearingTeacher ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eraser className="w-4 h-4" />} {t('timetable.clearTeacher', 'Clear teacher')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* clear-all confirm dialog with selectable scope */}
      <Dialog open={clearAllOpen} onOpenChange={(o) => { if (!clearingAll) setClearAllOpen(o); }}>
        <DialogContent data-testid="tt-clear-all-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-destructive"><Trash2 className="w-4 h-4" /> {t('timetable.clearAll', 'Clear All')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-muted-foreground">{t('timetable.clearAllIntro', 'Choose what to wipe. This permanently deletes the placed lessons — including locked and pinned rule entries — for the selected scope. This cannot be undone.')}</p>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('timetable.clearScope', 'Scope')}</Label>
              <Select value={clearScope} onValueChange={setClearScope} disabled={clearingAll}>
                <SelectTrigger className="h-9" data-testid="tt-clear-all-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="class" disabled={!classId || !sections.length}>
                    {classId && sections.length
                      ? `${t('timetable.thisClassOnly', 'This class only')} — ${classes.find((c) => c.id === classId)?.name || ''}`
                      : t('timetable.thisClassUnavailable', 'This class only (select a class first)')}
                  </SelectItem>
                  <SelectItem value="year">{t('timetable.wholeYear', 'Whole academic year (all classes & sections)')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {clearScope === 'class' && classId && sections.length > 0 && (
              <p className="text-xs text-muted-foreground">{t('timetable.clearAllClassNote', 'Clears all {n} section(s) of this class.').replace('{n}', String(sections.length))}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearAllOpen(false)} disabled={clearingAll} data-testid="tt-clear-all-cancel">{t('common.cancel', 'Cancel')}</Button>
            <Button className="text-destructive gap-2" variant="outline" onClick={doClearAll} disabled={clearingAll || (clearScope === 'class' && (!classId || !sections.length))} data-testid="tt-clear-all-confirm">
              {clearingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} {t('timetable.confirmClear', 'Delete permanently')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* auto-generate dialog with optional dual-program pairing */}
      <Dialog open={genOpen} onOpenChange={(o) => { if (!generating) setGenOpen(o); }}>
        <DialogContent data-testid="tt-generate-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4" /> {t('timetable.autoGenerate', 'Auto-Generate')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="rounded-md border border-border px-3 py-2 text-sm">
              <span className="text-muted-foreground">{t('timetable.primaryClass', 'Primary class')}: </span>
              <span className="font-medium">{viewLabel}</span>
            </div>
            <div className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2.5">
              <div className="pr-2">
                <p className="text-sm font-medium flex items-center gap-1.5"><ListOrdered className="w-4 h-4 text-primary" /> {t('timetable.generateCycle', 'Generate a specific cycle')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('timetable.generateCycleHint', 'Only rebuilds the classes assigned to the chosen cycle — other cycles are left completely untouched.')}</p>
              </div>
              <Select value={genCycle} onValueChange={setGenCycle}>
                <SelectTrigger className="h-9 w-44 shrink-0" data-testid="tt-gen-cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('timetable.wholeSchool', 'Whole school')}</SelectItem>
                  {cyclesList.map((c) => <SelectItem key={c.cycle} value={String(c.cycle)}>{c.name} ({(c.class_ids || []).length})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className={genCycle !== 'all' ? 'opacity-50 pointer-events-none' : ''}>

            <div className="flex items-start justify-between gap-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-2.5">
              <div className="pr-2">
                <p className="text-sm font-medium flex items-center gap-1.5"><CalendarRange className="w-4 h-4 text-primary" /> {t('timetable.genAll', 'Generate all classes together')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('timetable.genAllHint', 'Recommended. Builds every section in one pass (incl. paired FR/PT) so shared teachers are balanced across classes. Only lessons you locked stay in place.')}</p>
              </div>
              <Switch checked={genAll} onCheckedChange={setGenAll} data-testid="tt-gen-all" />
            </div>
            <div className={genAll ? 'opacity-50 pointer-events-none' : ''}>
              <p className="text-sm font-medium mb-1">{t('timetable.pairedProgram', 'Paired program (same students)')}</p>
              <p className="text-xs text-muted-foreground mb-2">{t('timetable.pairedHint', 'Optional. Co-schedules another class of the SAME pupils (e.g. FR + PT) so their lessons never overlap.')}</p>
              <div className="flex gap-2">
                <Select value={pairClassId} onValueChange={(v) => { setPairClassId(v); setPairSectionId(''); }} disabled={genAll}>
                  <SelectTrigger className="w-1/2" data-testid="tt-gen-pair-class"><SelectValue placeholder={t('timetable.selectClass', 'Select class')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('timetable.none', 'None (single class)')}</SelectItem>
                    {classes.filter((c) => c.id !== classId).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={pairSectionId} onValueChange={setPairSectionId} disabled={genAll || pairClassId === 'none' || !pairSections.length}>
                  <SelectTrigger className="w-1/2" data-testid="tt-gen-pair-section"><SelectValue placeholder={t('timetable.selectSection', 'Section')} /></SelectTrigger>
                  <SelectContent>{pairSections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {/* end cycle-dim wrapper (genAll + paired program) */}
            </div>
            <div className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2.5">
              <div className="pr-2">
                <p className="text-sm font-medium flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-primary" /> {t('timetable.respectWindows', 'Respect FR / PT program time windows')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('timetable.respectWindowsHint', 'Keeps French subjects in the FR time blocks and Portuguese subjects in the PT blocks for each day (per the school rule). Edit the blocks in Setup.')}</p>
              </div>
              <Switch checked={respectWindows} onCheckedChange={setRespectWindows} data-testid="tt-gen-respect-windows" />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2.5">
              <div className="pr-2">
                <p className="text-sm font-medium flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /> {t('timetable.searchEffort', 'Search effort')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('timetable.searchEffortHint', 'For very tight weeks, give the solver more time to re-arrange lessons before reporting conflicts.')}</p>
              </div>
              <Select value={String(solveTime)} onValueChange={(v) => setSolveTime(Number(v))}>
                <SelectTrigger className="h-9 w-40 shrink-0" data-testid="tt-gen-solve-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">{t('timetable.effortNormal', 'Normal (~20s)')}</SelectItem>
                  <SelectItem value="40">{t('timetable.effortThorough', 'Thorough (~40s)')}</SelectItem>
                  <SelectItem value="60">{t('timetable.effortMax', 'Max (~60s)')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {generating && (
            <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-3 space-y-2" data-testid="tt-gen-progress">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span data-testid="tt-gen-step">{genStep || t('timetable.stepSolving', 'Solving timetable…')}</span>
                <span className="ml-auto text-xs text-muted-foreground" data-testid="tt-gen-progress-pct">{genProgress}%</span>
              </div>
              <Progress value={genProgress} className="h-2" />
              <p className="text-xs text-muted-foreground">{t('timetable.genRunningHint', 'Running in the background — you can cancel any time. Results are only saved when the solve finishes.')}</p>
            </div>
          )}
          <DialogFooter>
            {generating ? (
              <Button variant="outline" onClick={cancelGenerate} className="gap-2" data-testid="tt-gen-cancel-run">
                <X className="w-4 h-4" /> {t('timetable.cancelGeneration', 'Cancel generation')}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setGenOpen(false)} data-testid="tt-gen-cancel">{t('common.cancel', 'Cancel')}</Button>
            )}
            <Button onClick={doGenerate} disabled={generating || (!genAll && pairClassId !== 'none' && !pairSectionId)} className="gap-2" data-testid="tt-gen-confirm">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {generating ? t('timetable.generating', 'Generating…') : t('timetable.generate', 'Generate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teacher availability editor (opened via Fix-It) */}
      <Dialog open={availOpen} onOpenChange={(o) => { if (!availSaving) setAvailOpen(o); }}>
        <DialogContent className="max-w-2xl" data-testid="tt-availability-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4" /> {t('timetable.teacherAvailability', 'Teacher availability')}{availTeacher?.name ? ` — ${availTeacher.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          {availLoading ? <InlineSpinner /> : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{t('timetable.availabilityHint', 'Tap a slot to mark the teacher UNAVAILABLE (red). Leave slots empty where they can teach. Then regenerate.')}</p>
              <div className="overflow-x-auto">
                <table className="border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="border border-border bg-muted/50 p-1.5 text-left">{t('timetable.period', 'Period')}</th>
                      {activeDays.map((d) => <th key={d} className="border border-border bg-muted/50 p-1.5 capitalize">{d}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {teachPeriods.map((p) => (
                      <tr key={p.seq}>
                        <td className="border border-border p-1.5 whitespace-nowrap text-muted-foreground">{p.label || `P${p.seq}`}</td>
                        {activeDays.map((d) => {
                          const blocked = availBlocked.has(`${d}:${p.seq}`);
                          return (
                            <td key={d} className="border border-border p-0">
                              <button type="button" onClick={() => toggleAvailCell(d, p.seq)} disabled={!canEdit}
                                data-testid={`tt-avail-${d}-${p.seq}`}
                                className={`w-full h-9 min-w-[64px] text-[11px] transition-colors ${blocked ? 'bg-destructive/15 text-destructive font-medium' : 'hover:bg-accent'}`}>
                                {blocked ? t('timetable.blocked', 'Blocked') : ''}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">{t('timetable.maxPerDay', 'Max periods/day')}</Label>
                <Input type="number" min="0" className="h-8 w-24" value={availMaxPerDay} onChange={(e) => setAvailMaxPerDay(e.target.value)}
                  placeholder={t('timetable.noLimit', 'No limit')} disabled={!canEdit} data-testid="tt-avail-max-per-day" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailOpen(false)} disabled={availSaving} data-testid="tt-avail-cancel">{t('common.cancel', 'Cancel')}</Button>
            {canEdit && (
              <Button onClick={saveAvailability} disabled={availSaving || availLoading} className="gap-2" data-testid="tt-avail-save">
                {availSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('common.save', 'Save')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={workloadOpen} onOpenChange={setWorkloadOpen}>
        <DialogContent className="max-w-lg" data-testid="tt-workload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="w-4 h-4" /> {t('timetable.workloadTitle', 'Teacher Workload')}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{t('timetable.workloadHint2', 'Assigned periods per week (from subject assignments) vs. placed in the timetable')}{workload ? ` · ${workload.total_slots} ${t('timetable.slotsPerWeek', 'slots/week')}` : ''}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground pt-1" data-testid="tt-workload-legend">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> {t('timetable.legendFull', 'Fully placed')}</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> {t('timetable.legendPartial', 'Partially placed')}</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-destructive" /> {t('timetable.legendRed', 'None placed / overloaded')}</span>
          </div>
          <div className="max-h-[55vh] overflow-y-auto mt-2 space-y-2 pr-1">
            {teacherLoad.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">{t('common.noData', 'No data')}</p>
            ) : teacherLoad.map((w) => {
              const assigned = w.assigned || 0;
              const placed = w.placed || 0;
              const unplaced = w.unplaced != null ? w.unplaced : Math.max(0, assigned - placed);
              const pct = assigned > 0 ? Math.min(100, Math.round((placed / assigned) * 100)) : 0;
              const status = w.status || (assigned === 0 ? 'idle' : (w.overloaded ? 'overloaded' : (placed === 0 ? 'none' : (placed >= assigned ? 'full' : 'partial'))));
              const barColor = status === 'full' ? 'bg-emerald-500'
                : status === 'partial' ? 'bg-amber-500'
                : status === 'idle' ? 'bg-muted-foreground/40'
                : 'bg-destructive'; // none / overloaded
              return (
                <div key={w.teacher_id} className="rounded-md border border-border px-3 py-2" data-testid={`tt-workload-row-${w.teacher_id}`}>
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <span className="font-medium text-sm truncate">{w.teacher_name}</span>
                    <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-1.5">
                      {w.overloaded && <AlertTriangle className="w-3.5 h-3.5 text-destructive" data-testid={`tt-workload-overloaded-${w.teacher_id}`} aria-label="overloaded" />}
                      {status === 'idle'
                        ? <span data-testid={`tt-workload-nums-${w.teacher_id}`}>{t('timetable.noAssignments', 'No assignments')}</span>
                        : <span data-testid={`tt-workload-nums-${w.teacher_id}`}>
                            <span className="text-foreground font-semibold">{placed}</span>/{assigned} {t('timetable.placedLower', 'placed')}
                            {unplaced > 0 && <span className={status === 'overloaded' ? 'text-destructive font-semibold' : 'text-amber-600 dark:text-amber-400 font-medium'}> · {unplaced} {t('timetable.unplacedLower', 'unplaced')}</span>}
                          </span>}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                    <div className={`h-full rounded-full ${barColor}`} style={{ width: `${assigned === 0 ? 0 : Math.max(pct, unplaced > 0 && placed === 0 ? 3 : pct)}%` }} data-testid={`tt-workload-bar-${w.teacher_id}`} />
                  </div>
                  {w.overloaded && (
                    <p className="text-[11px] text-destructive mt-1" data-testid={`tt-workload-overload-note-${w.teacher_id}`}>
                      {t('timetable.overloadNote', 'Overloaded')}: {assigned} {t('timetable.assignedLower', 'assigned')} &gt; {w.capacity} {t('timetable.availableSlots', 'available slots')}{w.blocked ? ` (${w.blocked} ${t('timetable.blockedLower', 'blocked')})` : ''}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* bulk export dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!bulkBusy) setBulkOpen(o); }}>
        <DialogContent data-testid="tt-bulk-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileStack className="w-4 h-4" /> {t('timetable.bulkExport', 'Bulk Export')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm font-medium mb-2">{t('timetable.bulkWhat', 'What to export')}</p>
              <RadioGroup value={bulkMode} onValueChange={setBulkMode} className="space-y-2">
                <label htmlFor="bulk-section" className="flex items-center gap-2 text-sm cursor-pointer" data-testid="tt-bulk-mode-section">
                  <RadioGroupItem value="section" id="bulk-section" /> {t('timetable.bulkAllSections', 'All classes / sections (one page each)')}
                </label>
                <label htmlFor="bulk-teacher" className="flex items-center gap-2 text-sm cursor-pointer" data-testid="tt-bulk-mode-teacher">
                  <RadioGroupItem value="teacher" id="bulk-teacher" /> {t('timetable.bulkAllTeachers', 'All teachers (one page each)')}
                </label>
              </RadioGroup>
            </div>
            {bulkMode === 'section' && (
              <div>
                <p className="text-sm font-medium mb-2">{t('timetable.bulkClassFilter', 'Limit to class (optional)')}</p>
                <Select value={bulkClass} onValueChange={setBulkClass}>
                  <SelectTrigger className="w-full" data-testid="tt-bulk-class"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('timetable.allClasses', 'All classes')}</SelectItem>
                    {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {bulkMode === 'teacher' && (
              <div>
                <p className="text-sm font-medium mb-2">{t('timetable.bulkTeacherFilter', 'Limit to teacher (optional)')}</p>
                <Select value={bulkTeacher} onValueChange={setBulkTeacher}>
                  <SelectTrigger className="w-full" data-testid="tt-bulk-teacher"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('timetable.allTeachers', 'All teachers')}</SelectItem>
                    {(teachers || []).map((tc) => <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="text-xs text-muted-foreground">{t('timetable.bulkNote', 'Schedules with no lessons are skipped. Each class/teacher is exported as its own single-page A4 PDF (content auto-scales to fit one page); multiple are bundled into a ZIP.')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy} data-testid="tt-bulk-cancel">{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runBulkExport} disabled={bulkBusy} className="gap-2" data-testid="tt-bulk-run">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} {t('timetable.export', 'Export')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fixed backdrop shown only while exporting, to hide the in-flow render target
          from the user. html2canvas still captures the target node directly. */}
      {bulkSheets.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.96)' }}
          className="flex items-center justify-center" data-testid="tt-bulk-overlay">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('timetable.preparingPdf', 'Preparing PDF…')}
          </div>
        </div>
      )}

      {/* Render target for bulk export. When active it is a normal in-flow block
          (mirrors the working single-view gridRef so html2canvas captures reliably);
          otherwise it is parked far off-screen. */}
      <div
        ref={bulkRef}
        aria-hidden="true"
        style={bulkSheets.length
          ? { width: '1400px', background: '#ffffff', padding: '16px' }
          : { position: 'absolute', left: '-10000px', top: 0, width: '1400px' }}
      >
        {bulkSheets.map((sh, i) => (
          <TimetableSheet key={sh.key} title={sh.title} subtitle={sh.subtitle} periods={periods}
            activeDays={activeDays} entries={sh.entries} mode={sh.mode} isLast={i === bulkSheets.length - 1} t={t}
            schoolName={schoolName} logoUrl={printLogo} yearName={yearName} />
        ))}
      </div>

      {/* Hidden render target for the single Export PDF (branded header + grid). */}
      <div
        ref={singleRef}
        aria-hidden="true"
        style={singleSheet
          ? { width: '1400px', background: '#ffffff', padding: '16px' }
          : { position: 'absolute', left: '-10000px', top: 0, width: '1400px' }}
      >
        {singleSheet && (
          <TimetableSheet title={singleSheet.title} subtitle={singleSheet.subtitle} periods={periods}
            activeDays={activeDays} entries={singleSheet.entries} mode={singleSheet.mode} isLast t={t}
            schoolName={schoolName} logoUrl={printLogo} yearName={yearName} />
        )}
      </div>
    </Card>
  );
}

/* ----------------------------- Timetable Sheet (bulk export) ----------------------------- */
/* Smart "Add lesson" dialog for the Teacher view. On open it loads THIS teacher's
   missing lessons for the selected year (requirement minus already-placed), shown
   as pills grouped by section. Selecting one and confirming validates (a) teacher
   free, (b) section free, (c) weekly cap not exceeded — then places it. There is
   no generic class/subject path, so ghost subjects can never be added. */
function TeacherAddDialog({ orgId, academicYearId, teacherId, teacherName, cell, onClose, onSaved, t }) {
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(''); setSelectedId(null);
    Promise.all([
      schoolApi.ttListLessons({ organization_id: orgId, teacher_id: teacherId, academic_year_id: academicYearId }),
      schoolApi.ttListEntries({ organization_id: orgId, teacher_id: teacherId, academic_year_id: academicYearId }),
    ]).then(([lessons, entries]) => {
      if (!alive) return;
      const placed = {};
      (entries || []).forEach((e) => { if (e.subject_id) { const k = `${e.section_id}:${e.subject_id}`; placed[k] = (placed[k] || 0) + 1; } });
      const miss = (lessons || []).map((l) => {
        const req = Number(l.periods_per_week || 0);
        const p = placed[`${l.section_id}:${l.subject_id}`] || 0;
        return { ...l, _required: req, _placed: p, _missing: Math.max(0, req - p) };
      }).filter((l) => l._missing > 0);
      setMissing(miss);
    }).catch(() => { if (alive) setMissing([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgId, teacherId, academicYearId]);

  const groups = useMemo(() => {
    const g = {};
    missing.forEach((l) => {
      const key = l.section_id || '__none__';
      if (!g[key]) g[key] = { section_id: key, label: [l.class_name, l.section_name].filter(Boolean).join(' — ') || l.section_name || l.class_name || '—', items: [] };
      g[key].items.push(l);
    });
    return Object.values(g).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [missing]);

  const selected = useMemo(() => missing.find((l) => l.id === selectedId) || null, [missing, selectedId]);

  const confirm = async () => {
    if (!selected || saving) return;
    setError('');
    // (c) weekly period count not exceeded
    if (selected._missing <= 0) { setError(t('timetable.capReached', 'All weekly periods for this subject are already placed.')); return; }
    setSaving(true);
    try {
      // (b) section has no other lesson at this slot
      const secEntries = await schoolApi.ttListEntries({ organization_id: orgId, section_id: selected.section_id, academic_year_id: academicYearId });
      if ((secEntries || []).some((e) => e.day === cell.day && e.period_seq === cell.seq)) {
        setError(t('timetable.sectionBusy', 'That section already has a lesson at this time slot.')); setSaving(false); return;
      }
      // (a) teacher clash + final cap are enforced server-side too
      await schoolApi.ttUpsertEntry({
        organization_id: orgId, academic_year_id: academicYearId,
        class_id: selected.class_id, section_id: selected.section_id,
        day: cell.day, period_seq: cell.seq, subject_id: selected.subject_id,
        uv_group: selected.uv_group || undefined, teacher_id: teacherId,
        lesson_id: selected.id || null, no_overwrite: true, enforce_lesson_cap: true,
      });
      toast.success(t('timetable.lessonAdded', 'Lesson added'));
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || t('timetable.couldNotPlace', 'Could not place lesson'));
      setSaving(false);
    }
  };

  const slotLabel = [cell.dayLabel || cell.day, cell.periodLabel].filter(Boolean).join(' · ');

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="tt-teacher-add-dialog">
        <DialogHeader>
          <DialogTitle>{t('timetable.addForTeacher', 'Add lesson')}{teacherName ? ` — ${teacherName}` : ''}</DialogTitle>
          {slotLabel && <p className="text-xs text-muted-foreground" data-testid="tt-ta-slot">{slotLabel}</p>}
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-xs text-muted-foreground">{t('timetable.pickMissing', "Pick one of this teacher's missing lessons to place in this slot.")}</p>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="tt-ta-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}
          <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
            {loading ? <InlineSpinner /> : missing.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="tt-ta-empty">{t('timetable.noMissingTeacher', 'This teacher has no missing lessons — every requirement is already placed.')}</p>
            ) : groups.map((g) => (
              <div key={g.section_id} data-testid={`tt-ta-group-${g.section_id}`}>
                <p className="text-[11px] font-medium text-muted-foreground mb-1">{g.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {g.items.map((l) => {
                    const active = selectedId === l.id;
                    return (
                      <button key={l.id} type="button" onClick={() => { setSelectedId(l.id); setError(''); }} aria-pressed={active}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${active ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-border bg-background hover:bg-accent text-foreground'}`}
                        data-testid={`tt-ta-lesson-${l.subject_id}`}>
                        <span className="truncate max-w-[180px]">{l.label || l.subject_name}</span>
                        <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-emerald-500/20' : 'bg-primary/10 text-primary'}`}>{l._missing} {t('timetable.hoursLeft', 'left')}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="tt-ta-cancel">{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={confirm} disabled={!selected || saving} className="gap-2" data-testid="tt-ta-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t('timetable.place', 'Place')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Branded PDF header — mirrors the Grade Report header (components/school/ReportCardView.jsx):
// ELI crest, school name, and a context line ("Timetable — <who> · Academic Year <year>").
const TimetablePdfHeader = ({ schoolName, logoUrl, line2 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '2px solid #1e3a5f', paddingBottom: '10px', marginBottom: '12px' }}>
    <img src={logoUrl || '/eli-crest.png'} alt={schoolName || 'Logo'} style={{ width: '56px', height: '56px', objectFit: 'contain' }} onError={(e) => { e.target.style.visibility = 'hidden'; }} />
    <div style={{ flex: 1, textAlign: 'center' }}>
      <h2 style={{ fontSize: '16px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', color: '#1e3a5f', lineHeight: 1.2, margin: 0 }}>{schoolName || 'School'}</h2>
      <p style={{ fontSize: '12px', fontWeight: 600, marginTop: '2px', color: '#0f172a' }}>{line2}</p>
    </div>
    <div style={{ width: '56px' }} />
  </div>
);

function TimetableSheet({ title, subtitle, periods, activeDays, entries, mode, t, schoolName, logoUrl, yearName }) {
  const map = {};
  (entries || []).forEach((e) => { map[cellKey(e.day, e.period_seq)] = e; });
  const line2 = `${t('nav.timetable', 'Timetable')}${title ? ` — ${title}` : ''}${yearName ? ` · ${t('timetable.academicYear', 'Academic Year')} ${yearName}` : ''}`;
  // Fixed table layout with equal, page-fitting columns. This guarantees EVERY
  // active day column (incl. Friday) fits inside the captured page width — dense
  // dual-program (FR/PT) content used to overflow the 1400px capture box and get
  // clipped on the right, dropping the last column and truncating text.
  const periodPct = 11;
  const dayPct = activeDays.length ? (100 - periodPct) / activeDays.length : 100 - periodPct;
  const wrap = { wordBreak: 'break-word', overflowWrap: 'anywhere' };
  // Density-adaptive row height: the more periods, the shorter each row so the whole
  // sheet stays compact and needs little/no down-scaling to fit ONE A4 landscape page.
  const n = (periods || []).length;
  const rowMinH = n >= 13 ? 26 : n >= 11 ? 32 : n >= 9 ? 40 : 52;
  const cellStyle = { ...wrap, height: `${rowMinH}px` };
  return (
    <div className="tt-sheet" data-tt-sheet="1" style={{ padding: '4px 8px 8px' }}>
      <TimetablePdfHeader schoolName={schoolName} logoUrl={logoUrl} line2={line2} />
      {subtitle && <p className="text-xs text-muted-foreground px-1 pb-2" style={{ textAlign: 'center' }}>{subtitle}</p>}
      <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr>
            <th className="border border-border bg-muted/50 p-2 text-xs" style={{ width: `${periodPct}%` }}>{t('timetable.period', 'Period')}</th>
            {activeDays.map((d) => <th key={d.key} className="border border-border bg-muted/50 p-2 text-xs" style={{ width: `${dayPct}%` }}>{d.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {periods.map((p) => (
            <tr key={p.seq} className={p.is_break ? 'bg-muted/30' : ''}>
              <td className="border border-border p-2 text-xs text-center" style={wrap}>
                <div className="font-medium">{p.label}</div>
                {p.start && <div className="text-[10px] text-muted-foreground">{p.start}–{p.end}</div>}
              </td>
              {activeDays.map((d) => {
                if (p.is_break) return <td key={d.key} className="border border-border p-1 text-center text-[10px] text-muted-foreground">{t('timetable.break', 'Break')}</td>;
                if (!periodRunsOn(p, d.key)) return <td key={d.key} className="border border-border p-1 text-center text-[10px] text-muted-foreground/40 bg-muted/40">—</td>;
                const e = map[cellKey(d.key, p.seq)];
                return (
                  <td key={d.key} className="border border-border p-1 text-center align-middle" style={cellStyle}>
                    {e ? (
                      <div>
                        <div className="font-medium text-xs leading-tight" style={wrap}>{mode === 'teacher' ? [e.class_name, e.section_name].filter(Boolean).join(' — ') : (e.label || e.subject_name)}</div>
                        <div className="text-[10px] text-muted-foreground" style={wrap}>{mode === 'teacher' ? (e.label || e.subject_name) : mode === 'student' ? [e.class_name, e.teacher_name].filter(Boolean).join(' · ') : e.teacher_name}</div>
                      </div>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------------------- AI Assistant ----------------------------- */
function AiAssistant({ orgId, academicYearId, classes, config, activeDays, canEdit, t }) {
  const [classId, setClassId] = useState('');
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState('');
  const [messages, setMessages] = useState([]); // {role, text, applied, failed}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!classId) { setSections([]); setSectionId(''); return; }
    schoolApi.listSections(orgId, classId, academicYearId).then((s) => { setSections(s || []); setSectionId((s && s[0]?.id) || ''); });
  }, [classId, orgId, academicYearId]);

  const send = async () => {
    const msg = input.trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setInput(''); setBusy(true);
    try {
      const res = await schoolApi.ttAssistant({ organization_id: orgId, section_id: sectionId || undefined, message: msg });
      setMessages((m) => [...m, { role: 'assistant', text: res.explanation || '', applied: res.applied || [], failed: res.failed || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: e?.response?.data?.detail || 'AI assistant request failed.', applied: [], failed: [] }]);
    } finally { setBusy(false); }
  };

  // Run the deterministic generator for the selected section, then let the user
  // refine the result in plain language via the chat below.
  const aiGenerate = async () => {
    if (!sectionId) { toast.error(t('timetable.selectSectionFirst', 'Select a class and section first')); return; }
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', text: t('timetable.aiGenReq', 'Auto-generate the timetable for this section.') }]);
    try {
      // Enqueue the background solve (202 + job id), then poll until it finishes.
      const { job_id: jobId } = await schoolApi.ttGenerate({ organization_id: orgId, academic_year_id: academicYearId, section_ids: [sectionId], seed: Math.floor(Math.random() * 1000) });
      if (!jobId) throw new Error('No job id returned');
      let res = null;
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 1200));
        let job;
        try { job = await schoolApi.ttGenerateStatus(jobId); } catch (_e) { continue; }
        if (job.status === 'completed') { res = job.result || {}; break; }
        if (job.status === 'failed') { throw new Error(job.error || 'Generation failed'); }
        if (job.status === 'cancelled' || job.status === 'superseded') { throw new Error(t('timetable.genInterrupted', 'Generation was interrupted by a newer run.')); }
      }
      if (!res) throw new Error(t('timetable.genTimeout', 'Generation timed out — try a shorter search time.'));
      const un = res.unplaced || [];
      const head = `${t('timetable.placed', 'Placed')} ${res.placed} · ${t('timetable.unplaced', 'Unplaced')} ${un.length}.`;
      const tail = un.length
        ? ` ${t('timetable.unplacedMsg', 'lesson period(s) could not be placed:')} ${un.map((u) => u.label || u.subject_name).join(', ')}. ${t('timetable.aiGenTip', 'Tell me to relax a rule (e.g. remove a Max/day or allow doubles) and I will regenerate.')}`
        : ` ${t('timetable.aiGenDone', 'You can now ask me to move, swap or balance lessons.')}`;
      setMessages((m) => [...m, { role: 'assistant', text: head + tail, applied: [], failed: [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: e?.response?.data?.detail || 'Generation failed.', applied: [], failed: [] }]);
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5" data-testid="tt-assistant-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Sparkles className="w-5 h-5 text-primary" />
        <p className="text-sm text-muted-foreground flex-1 min-w-[200px]">{t('timetable.aiHint', 'Auto-generate a full timetable, then refine in plain language — e.g. "move Math to Monday morning" or "spread U.V1 across the week".')}</p>
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="w-44" data-testid="tt-ai-class"><SelectValue placeholder={t('timetable.allSections', 'All sections')} /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sectionId} onValueChange={setSectionId} disabled={!sections.length}>
          <SelectTrigger className="w-36" data-testid="tt-ai-section"><SelectValue placeholder={t('timetable.section', 'Section')} /></SelectTrigger>
          <SelectContent>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={aiGenerate} disabled={!canEdit || busy || !sectionId} size="sm" className="gap-2" data-testid="tt-ai-generate">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {t('timetable.aiAutoGenerate', 'AI Auto-Generate')}
        </Button>
      </div>

      <div className="border border-border rounded-lg h-[340px] overflow-y-auto p-3 space-y-3 bg-muted/20" data-testid="tt-ai-messages">
        {messages.length === 0 && <p className="text-sm text-muted-foreground text-center mt-12">{t('timetable.aiEmpty', 'Pick a class + section and hit AI Auto-Generate, then chat to fine-tune.')}</p>}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border'}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {(m.applied?.length > 0) && <div className="mt-2 space-y-1">{m.applied.map((a, j) => <div key={j} className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {a.action?.type}: {a.message}</div>)}</div>}
              {(m.failed?.length > 0) && <div className="mt-2 space-y-1">{m.failed.map((a, j) => <div key={j} className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {a.action?.type}: {a.message}</div>)}</div>}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-card border border-border rounded-lg px-3 py-2 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t('timetable.thinking', 'Thinking…')}</div></div>}
      </div>

      <div className="flex gap-2 mt-3">
        <Textarea value={input} onChange={(e) => setInput(e.target.value)} disabled={!canEdit || busy} rows={1}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t('timetable.aiPlaceholder', 'Type a request…')} className="resize-none" data-testid="tt-ai-input" />
        <Button onClick={send} disabled={!canEdit || busy || !input.trim()} className="gap-2" data-testid="tt-ai-send"><Send className="w-4 h-4" /> {t('common.send', 'Send')}</Button>
      </div>
    </Card>
  );
}
